/**
 * Corner Renderer - Pre-computes wall edge trim points for clean mitered corners
 * Handles L-corners (two endpoints meeting) and T-intersections (endpoint meets wall body)
 * Also handles interior finish lines (drywall) and exterior finish (siding)
 */

import { WALL_THICKNESS_OPTIONS, WALL_LAYERS } from '../../../constants/walls';
import { GRID_SIZE } from '../../../constants/grid';
import { adjustColor } from '../../../utils/geometry';

// Debug logging - set to true to enable verbose output
const DEBUG = false;  // L-corner/general debug
const DEBUG_T_JUNCTION = false;  // T-junction specific debug

// Convert inches to pixels
const inchesToPixels = (inches) => inches * (GRID_SIZE / 6);

// Get finish line offsets for a wall type (in pixels from centerline)
const getFinishOffsets = (wallType) => {
  const config = WALL_LAYERS[wallType] || WALL_LAYERS['interior'];
  const thicknessInches = WALL_THICKNESS_OPTIONS[wallType]?.thickness || 6;
  const totalThickness = inchesToPixels(thicknessInches);
  const halfThick = totalThickness / 2;

  // Calculate layer positions
  const layers = config.layers || [];
  const totalLayerThickness = layers.reduce((sum, l) => sum + l.thickness, 0);
  const scaleFactor = totalThickness / totalLayerThickness;

  // Find drywall positions (inside finish)
  // For exterior walls: siding is on left (outside), drywall on right (inside)
  // For interior walls: drywall on both sides
  const isExterior = wallType?.includes('exterior');
  const isPostFrame = wallType?.startsWith('post-');

  let leftFinishOffset = null;  // Offset from center for left finish line
  let rightFinishOffset = null; // Offset from center for right finish line

  if (isPostFrame) {
    // Post-frame: Metal Siding on left (outside), Interior finish on right
    let offset = -halfThick;
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      const layerThick = layer.thickness * scaleFactor;
      if (i === 0 && layer.name === 'Metal Siding') {
        leftFinishOffset = offset + layerThick;
      }
      if (i === layers.length - 1 && layer.name === 'Interior') {
        rightFinishOffset = offset;
      }
      offset += layerThick;
    }
  } else if (isExterior) {
    // Left side: find where siding/sheathing meets (after siding layer)
    // Right side: find where drywall starts (before drywall layer)
    let offset = -halfThick;
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      const layerThick = layer.thickness * scaleFactor;
      if (i === 0 && (layer.name === 'Siding' || layer.pattern === 'siding')) {
        // After siding layer
        leftFinishOffset = offset + layerThick;
      }
      if (layer.name === 'Drywall' && i === layers.length - 1) {
        // Before last drywall layer
        rightFinishOffset = offset;
      }
      offset += layerThick;
    }
  } else {
    // Interior wall - drywall on both sides
    let offset = -halfThick;
    for (let i = 0; i < layers.length; i++) {
      const layer = layers[i];
      const layerThick = layer.thickness * scaleFactor;
      if (layer.name === 'Drywall') {
        if (i === 0) {
          // First drywall - inside of left edge
          leftFinishOffset = offset + layerThick;
        } else if (i === layers.length - 1) {
          // Last drywall - inside of right edge
          rightFinishOffset = offset;
        }
      }
      offset += layerThick;
    }
  }

  return { leftFinishOffset, rightFinishOffset, isExterior, isPostFrame };
};

// Distance between two points
const dist = (p1, p2) => Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);

// Check if two points are close enough to be considered connected
// Using a larger tolerance to account for grid snapping and wall thickness variations
// Increased to handle walls of different thicknesses meeting (e.g., 2x4 interior at 2x6 exterior)
const pointsClose = (p1, p2, tolerance = 40) => dist(p1, p2) < tolerance;

// Check if a point is on a line segment (not at endpoints)
// Returns the t parameter (0-1) if on segment, or null if not
// Takes into account the host wall's thickness - the incoming wall might snap to
// the edge of the host wall rather than exactly on its centerline
const pointOnSegmentT = (pt, segStart, segEnd, hostWallThickness = 20, incomingWallThickness = 20) => {
  const segLen = dist(segStart, segEnd);
  if (segLen === 0) return null;

  const dx = pt.x - segStart.x;
  const dy = pt.y - segStart.y;
  const segDx = segEnd.x - segStart.x;
  const segDy = segEnd.y - segStart.y;

  // Project pt onto segment line
  const t = (dx * segDx + dy * segDy) / (segLen * segLen);

  // Must be within segment (not at endpoints) - use slightly looser bounds
  if (t < 0.02 || t > 0.98) return null;

  // Find closest point on segment
  const closestX = segStart.x + t * segDx;
  const closestY = segStart.y + t * segDy;

  // Tolerance should account for:
  // - Half of host wall thickness (incoming might be at host edge)
  // - Half of incoming wall thickness (incoming centerline might be beyond host edge but wall body overlaps)
  // - Additional margin for grid snapping imprecision (grid is 20 pixels = 6")
  // The incoming wall's centerline might snap to a grid point beyond the host wall's far edge,
  // but the wall bodies should still be considered intersecting if they're within reasonable distance
  const tolerance = Math.max(
    (hostWallThickness + incomingWallThickness) / 2 + 15,  // Combined half-thicknesses plus grid margin
    40  // Minimum tolerance to handle grid snapping with thin walls
  );

  if (dist(pt, { x: closestX, y: closestY }) < tolerance) {
    return t;
  }
  return null;
};

// Check if there's a collinear wall at a junction point
// Returns the collinear wall if found, null otherwise
const findCollinearWallAtJunction = (walls, wall, wallEnd, junctionPt, wallDir, geos, tolerance = 40) => {
  for (const otherWall of walls) {
    if (otherWall.id === wall.id) continue;

    const otherGeo = geos[otherWall.id];
    if (!otherGeo) continue;

    // Check if other wall has an endpoint at the junction
    const otherAtStart = dist(otherWall.start, junctionPt) < tolerance;
    const otherAtEnd = dist(otherWall.end, junctionPt) < tolerance;
    if (!otherAtStart && !otherAtEnd) continue;

    // Check if other wall is collinear with the given wall (cross product near zero)
    const otherDir = { x: otherGeo.dirX, y: otherGeo.dirY };
    const cross = Math.abs(wallDir.x * otherDir.y - wallDir.y * otherDir.x);

    if (cross < 0.001) {
      // Found a collinear wall at this junction
      return { wall: otherWall, end: otherAtStart ? 'start' : 'end' };
    }
  }
  return null;
};

// Get wall geometry info in world coordinates
const getWallGeometry = (wall) => {
  const thicknessInches = WALL_THICKNESS_OPTIONS[wall.type]?.thickness || 8;
  const thickness = inchesToPixels(thicknessInches);
  const halfThick = thickness / 2;

  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const len = Math.sqrt(dx * dx + dy * dy);

  if (len === 0) return null;

  // Unit vectors
  const dirX = dx / len;
  const dirY = dy / len;
  const perpX = -dirY; // perpendicular (90 deg CCW)
  const perpY = dirX;

  // Get finish line offsets
  const finishOffsets = getFinishOffsets(wall.type);
  const { leftFinishOffset, rightFinishOffset, isExterior } = finishOffsets;

  // If wall is flipped, swap the finish offsets
  const actualLeftFinish = wall.flipped ? (rightFinishOffset !== null ? -rightFinishOffset : null) : leftFinishOffset;
  const actualRightFinish = wall.flipped ? (leftFinishOffset !== null ? -leftFinishOffset : null) : rightFinishOffset;

  const geo = {
    wall,
    thickness,
    halfThick,
    len,
    dirX,
    dirY,
    perpX,
    perpY,
    isExterior,
    // Wall edge endpoints in world coordinates
    startLeft: { x: wall.start.x + perpX * halfThick, y: wall.start.y + perpY * halfThick },
    startRight: { x: wall.start.x - perpX * halfThick, y: wall.start.y - perpY * halfThick },
    endLeft: { x: wall.end.x + perpX * halfThick, y: wall.end.y + perpY * halfThick },
    endRight: { x: wall.end.x - perpX * halfThick, y: wall.end.y - perpY * halfThick },
  };

  // Add finish line endpoints if applicable
  // Left finish is on the positive perpendicular side (CCW from wall direction)
  // Right finish is on the negative perpendicular side
  if (actualLeftFinish !== null) {
    geo.startLeftFinish = { x: wall.start.x + perpX * actualLeftFinish, y: wall.start.y + perpY * actualLeftFinish };
    geo.endLeftFinish = { x: wall.end.x + perpX * actualLeftFinish, y: wall.end.y + perpY * actualLeftFinish };
    geo.leftFinishOffset = actualLeftFinish;
  }
  if (actualRightFinish !== null) {
    geo.startRightFinish = { x: wall.start.x + perpX * actualRightFinish, y: wall.start.y + perpY * actualRightFinish };
    geo.endRightFinish = { x: wall.end.x + perpX * actualRightFinish, y: wall.end.y + perpY * actualRightFinish };
    geo.rightFinishOffset = actualRightFinish;
  }

  return geo;
};

// Find intersection of two infinite lines (p1 + t*d1) and (p2 + s*d2)
const lineIntersect = (p1, d1, p2, d2) => {
  const denom = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(denom) < 0.0001) return null;
  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / denom;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
};


/**
 * Compute trim data for all walls
 */
export function computeWallTrims(walls) {
  if (!walls || walls.length === 0) return {};

  if (DEBUG) {
    walls.forEach(w => {
    });
  }

  const trims = {};
  const geos = {};

  // Initialize trim data and geometry for each wall
  walls.forEach(wall => {
    const geo = getWallGeometry(wall);
    if (!geo) return;

    geos[wall.id] = geo;
    trims[wall.id] = {
      startLeftTrim: null,
      startRightTrim: null,
      endLeftTrim: null,
      endRightTrim: null,
      // Finish line trims
      startLeftFinishTrim: null,
      startRightFinishTrim: null,
      endLeftFinishTrim: null,
      endRightFinishTrim: null,
      // For T-intersections: gaps in the host wall's edges
      // Each gap is { start: point, end: point } representing where to break the edge line
      leftEdgeGaps: [],
      rightEdgeGaps: [],
      leftFinishGaps: [],
      rightFinishGaps: [],
      // Interior T-junction connectors: short lines connecting incoming finish to host finish
      interiorTConnectors: [],
      geo,
    };
  });

  // Find L-corners and T-intersections
  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const wall1 = walls[i];
      const wall2 = walls[j];
      const geo1 = geos[wall1.id];
      const geo2 = geos[wall2.id];

      if (!geo1 || !geo2) continue;

      // Check all endpoint combinations for L-corners
      const checks = [
        { w1End: 'start', w2End: 'start', pt1: wall1.start, pt2: wall2.start },
        { w1End: 'start', w2End: 'end', pt1: wall1.start, pt2: wall2.end },
        { w1End: 'end', w2End: 'start', pt1: wall1.end, pt2: wall2.start },
        { w1End: 'end', w2End: 'end', pt1: wall1.end, pt2: wall2.end },
      ];

      for (const check of checks) {
        const distance = dist(check.pt1, check.pt2);
        if (!pointsClose(check.pt1, check.pt2)) {
          // Log near-misses (endpoints that are close but not quite)
          if (distance < 100 && DEBUG) {
          }
          continue;
        }
        // Found an L-corner - compute miter intersection
        // Direction vectors for the walls (always in the wall's natural direction: start -> end)
        const d1 = { x: geo1.dirX, y: geo1.dirY };
        const d2 = { x: geo2.dirX, y: geo2.dirY };

        const w1Left = check.w1End === 'start' ? geo1.startLeft : geo1.endLeft;
        const w1Right = check.w1End === 'start' ? geo1.startRight : geo1.endRight;
        const w2Left = check.w2End === 'start' ? geo2.startLeft : geo2.endLeft;
        const w2Right = check.w2End === 'start' ? geo2.startRight : geo2.endRight;

        const trim1Key = check.w1End === 'start' ? 'start' : 'end';
        const trim2Key = check.w2End === 'start' ? 'start' : 'end';

        // Determine effective direction vectors pointing AWAY from the corner for each wall
        // If wall's start is at corner, direction away is toward end (positive dir)
        // If wall's end is at corner, direction away is toward start (negative dir)
        const dir1Away = check.w1End === 'start'
          ? { x: d1.x, y: d1.y }   // start at corner -> away is toward end
          : { x: -d1.x, y: -d1.y }; // end at corner -> away is toward start
        const dir2Away = check.w2End === 'start'
          ? { x: d2.x, y: d2.y }
          : { x: -d2.x, y: -d2.y };

        // Use cross product of "away" directions to determine turning direction
        // Cross > 0: wall2 is to the left (CCW) of wall1
        // Cross < 0: wall2 is to the right (CW) of wall1
        const cross = dir1Away.x * dir2Away.y - dir1Away.y * dir2Away.x;

        // Compute all four possible edge intersections
        const intLL = lineIntersect(w1Left, d1, w2Left, d2);
        const intRR = lineIntersect(w1Right, d1, w2Right, d2);
        const intLR = lineIntersect(w1Left, d1, w2Right, d2);
        const intRL = lineIntersect(w1Right, d1, w2Left, d2);

        // Helper to check if intersection is reasonably close to corner
        const maxDist = Math.max(geo1.halfThick, geo2.halfThick) * 8;
        const isReasonable = (pt) => pt && dist(pt, check.pt1) < maxDist;

        // For L-corner miter, we use same-side intersections (LL and RR)
        // These extend the wall edges until they meet, creating a proper miter joint
        // The key insight: both walls share the same "left" and "right" edges at the corner
        // because left/right is defined relative to the wall's direction vector
        //
        // EXCEPTION: When an exterior wall meets an interior wall at an L-corner,
        // the exterior wall's OUTER edge (siding side) should NOT be mitered.
        // The exterior siding should continue straight past the corner.
        // Only the interior-facing edges should be mitered.

        if (Math.abs(cross) > 0.001 && intLL && intRR) {
          // Check for 3-way junction: perpendicular wall meeting where two collinear walls join
          // In this case, don't apply L-corner miter to the perpendicular - treat it as T-junction
          const wall1CollinearPartner = findCollinearWallAtJunction(walls, wall1, check.w1End, check.pt1, d1, geos);
          const wall2CollinearPartner = findCollinearWallAtJunction(walls, wall2, check.w2End, check.pt2, d2, geos);


          if (wall1CollinearPartner || wall2CollinearPartner) {
            // This is a 3-way junction - one wall has a collinear partner at this point
            // The OTHER wall is perpendicular and should be treated as T-junction, not L-corner
            const perpWall = wall1CollinearPartner ? wall2 : wall1;
            const perpGeo = wall1CollinearPartner ? geo2 : geo1;
            const perpDir = wall1CollinearPartner ? d2 : d1;
            const perpTrimKey = wall1CollinearPartner ? trim2Key : trim1Key;
            const perpEnd = wall1CollinearPartner ? check.w2End : check.w1End;
            const hostWall = wall1CollinearPartner ? wall1 : wall2;
            const hostGeo = wall1CollinearPartner ? geo1 : geo2;
            const hostDir = wall1CollinearPartner ? d1 : d2;
            const hostEnd = wall1CollinearPartner ? check.w1End : check.w2End;

            // NOTE: Don't skip even if perpendicular already has trims from T-junction processing.
            // 3-way junction requires the perpendicular to extend to the FAR edge (not near edge like T-junction).
            // We will OVERRIDE any existing trims.
            const hadExistingTrims = trims[perpWall.id][`${perpTrimKey}LeftTrim`] && trims[perpWall.id][`${perpTrimKey}RightTrim`];


            // Get perpendicular wall's edge points at its junction end
            const perpLeft = perpEnd === 'start' ? perpGeo.startLeft : perpGeo.endLeft;
            const perpRight = perpEnd === 'start' ? perpGeo.startRight : perpGeo.endRight;

            // Get host wall's edge points at the junction (not at wrong end!)
            const hostLeftAtJunction = hostEnd === 'start' ? hostGeo.startLeft : hostGeo.endLeft;
            const hostRightAtJunction = hostEnd === 'start' ? hostGeo.startRight : hostGeo.endRight;

            // Determine which side of host wall the perpendicular approaches from
            // Use the perpendicular wall's center (away from junction) to determine approach side
            const perpCenter = perpEnd === 'start' ? perpWall.end : perpWall.start;

            // Distance from perpendicular center to each host edge line
            const distToLeft = Math.abs(
              (hostGeo.endLeft.y - hostGeo.startLeft.y) * perpCenter.x -
              (hostGeo.endLeft.x - hostGeo.startLeft.x) * perpCenter.y +
              hostGeo.endLeft.x * hostGeo.startLeft.y -
              hostGeo.endLeft.y * hostGeo.startLeft.x
            ) / dist(hostGeo.startLeft, hostGeo.endLeft);

            const distToRight = Math.abs(
              (hostGeo.endRight.y - hostGeo.startRight.y) * perpCenter.x -
              (hostGeo.endRight.x - hostGeo.startRight.x) * perpCenter.y +
              hostGeo.endRight.x * hostGeo.startRight.y -
              hostGeo.endRight.y * hostGeo.startRight.x
            ) / dist(hostGeo.startRight, hostGeo.endRight);

            // The near edge is the one closer to perpCenter (the side perp approaches from)
            // The far edge is on the opposite side
            const nearEdgePt = distToLeft < distToRight ? hostLeftAtJunction : hostRightAtJunction;
            const farEdgePt = distToLeft < distToRight ? hostRightAtJunction : hostLeftAtJunction;
            const side = distToLeft < distToRight ? 1 : -1; // 1 = approaching from left, -1 = from right

            // For 3-way junction: perpendicular extends through to the FAR edge (not near edge like T-junction)
            // because the host wall is broken and there's a gap for the perpendicular to pass through
            const leftTrim = lineIntersect(perpLeft, perpDir, farEdgePt, hostDir);
            const rightTrim = lineIntersect(perpRight, perpDir, farEdgePt, hostDir);

            if (leftTrim) {
              trims[perpWall.id][`${perpTrimKey}LeftTrim`] = leftTrim;
            }
            if (rightTrim) {
              trims[perpWall.id][`${perpTrimKey}RightTrim`] = rightTrim;
            }

            // Mark perpendicular wall's end as T-junction (suppress end cap)
            trims[perpWall.id][`${perpTrimKey}HasT`] = true;

            // Mark this wall as involved in a 3-way junction - this prevents T-junction code
            // from creating connectors for it later (since the perp passes THROUGH, not stops at near side)
            trims[perpWall.id].is3WayJunction = true;

            // Create gaps in BOTH near and far edges of host/collinear walls where perpendicular passes through
            const collinearPartner = (wall1CollinearPartner || wall2CollinearPartner).wall;

            // Compute near edge intersections for gap creation (separate from far edge trims)
            const nearLeftIntersect = lineIntersect(perpLeft, perpDir, nearEdgePt, hostDir);
            const nearRightIntersect = lineIntersect(perpRight, perpDir, nearEdgePt, hostDir);

            // Create gaps in NEAR edge
            if (nearLeftIntersect && nearRightIntersect) {
              if (side > 0) {
                trims[hostWall.id].leftEdgeGaps.push({ start: nearLeftIntersect, end: nearRightIntersect, t: 1.0 });
                trims[collinearPartner.id].leftEdgeGaps.push({ start: nearLeftIntersect, end: nearRightIntersect, t: 0.0 });
              } else {
                trims[hostWall.id].rightEdgeGaps.push({ start: nearLeftIntersect, end: nearRightIntersect, t: 1.0 });
                trims[collinearPartner.id].rightEdgeGaps.push({ start: nearLeftIntersect, end: nearRightIntersect, t: 0.0 });
              }
            }

            // Create gaps in FAR edge (using the leftTrim/rightTrim which are far edge intersections)
            if (leftTrim && rightTrim) {
              if (side > 0) {
                trims[hostWall.id].rightEdgeGaps.push({ start: leftTrim, end: rightTrim, t: 1.0 });
                trims[collinearPartner.id].rightEdgeGaps.push({ start: leftTrim, end: rightTrim, t: 0.0 });
              } else {
                trims[hostWall.id].leftEdgeGaps.push({ start: leftTrim, end: rightTrim, t: 1.0 });
                trims[collinearPartner.id].leftEdgeGaps.push({ start: leftTrim, end: rightTrim, t: 0.0 });
              }
            }

            // Handle finish lines (sheetrock/drywall) for the 3-way junction
            // For interior walls, finish lines are on BOTH sides (left and right)
            const perpLeftFinish = perpEnd === 'start' ? perpGeo.startLeftFinish : perpGeo.endLeftFinish;
            const perpRightFinish = perpEnd === 'start' ? perpGeo.startRightFinish : perpGeo.endRightFinish;
            const hostLeftFinish = hostEnd === 'start' ? hostGeo.startLeftFinish : hostGeo.endLeftFinish;
            const hostRightFinish = hostEnd === 'start' ? hostGeo.startRightFinish : hostGeo.endRightFinish;


            // For interior-to-interior junction: perpendicular finish lines trim to host's FAR finish line
            // (the one on the opposite side from where perp enters)
            // Use hostLeftFinish/hostRightFinish which are already computed based on hostEnd
            const nearFinishPt = side > 0 ? hostLeftFinish : hostRightFinish;
            const farFinishPt = side > 0 ? hostRightFinish : hostLeftFinish;


            // For perpendicular that PASSES THROUGH (extends beyond junction), create GAPS in finish lines
            // For perpendicular that ENDS at junction, create TRIMS
            // Check if perpendicular extends beyond the junction by comparing its endpoint to junction point
            const junctionPt = check.pt1; // The junction point
            const perpOtherEnd = perpEnd === 'start' ? perpWall.end : perpWall.start;
            const perpJunctionEnd = perpEnd === 'start' ? perpWall.start : perpWall.end;

            // Check if the perpendicular extends beyond the junction (passes through)
            // by seeing if the junction point is between the perpendicular's two endpoints
            const perpLength = dist(perpWall.start, perpWall.end);
            const distToJunction = dist(perpJunctionEnd, junctionPt);
            const perpPassesThrough = distToJunction > 5; // If junction end isn't exactly at junction, wall passes through


            if (perpPassesThrough && nearEdgePt && farEdgePt && perpLeftFinish && perpRightFinish) {
              // Perpendicular passes through - create GAPS instead of trims
              // Use host BODY edges (not finish lines) for gap boundaries so the gap
              // covers the full host wall width (body is wider than finish)
              const nearLeftInt = lineIntersect(perpLeftFinish, perpDir, nearEdgePt, hostDir);
              const nearRightInt = lineIntersect(perpRightFinish, perpDir, nearEdgePt, hostDir);
              const farLeftInt = lineIntersect(perpLeftFinish, perpDir, farEdgePt, hostDir);
              const farRightInt = lineIntersect(perpRightFinish, perpDir, farEdgePt, hostDir);

              if (nearLeftInt && farLeftInt) {
                // Order gap points along perpendicular direction
                const tNear = (nearLeftInt.x - perpLeftFinish.x) * perpDir.x + (nearLeftInt.y - perpLeftFinish.y) * perpDir.y;
                const tFar = (farLeftInt.x - perpLeftFinish.x) * perpDir.x + (farLeftInt.y - perpLeftFinish.y) * perpDir.y;
                const gapStart = tNear < tFar ? nearLeftInt : farLeftInt;
                const gapEnd = tNear < tFar ? farLeftInt : nearLeftInt;
                trims[perpWall.id].leftFinishGaps.push({ start: gapStart, end: gapEnd });
              }
              if (nearRightInt && farRightInt) {
                const tNear = (nearRightInt.x - perpRightFinish.x) * perpDir.x + (nearRightInt.y - perpRightFinish.y) * perpDir.y;
                const tFar = (farRightInt.x - perpRightFinish.x) * perpDir.x + (farRightInt.y - perpRightFinish.y) * perpDir.y;
                const gapStart = tNear < tFar ? nearRightInt : farRightInt;
                const gapEnd = tNear < tFar ? farRightInt : nearRightInt;
                trims[perpWall.id].rightFinishGaps.push({ start: gapStart, end: gapEnd });
              }
            } else {
              // Perpendicular ENDS at junction (doesn't pass through)
              // Trim to NEAR finish (where it enters/meets the host), NOT far finish
              if (nearFinishPt && perpLeftFinish) {
                const leftFinishTrim = lineIntersect(perpLeftFinish, perpDir, nearFinishPt, hostDir);
                if (leftFinishTrim) {
                  trims[perpWall.id][`${perpTrimKey}LeftFinishTrim`] = leftFinishTrim;
                }
              }
              if (nearFinishPt && perpRightFinish) {
                const rightFinishTrim = lineIntersect(perpRightFinish, perpDir, nearFinishPt, hostDir);
                if (rightFinishTrim) {
                  trims[perpWall.id][`${perpTrimKey}RightFinishTrim`] = rightFinishTrim;
                }
              }
            }

            // For 3-way junction: trim the HOST and COLLINEAR PARTNER finish lines
            // Host wall's finish lines END at perpendicular's near edge
            // Collinear partner's finish lines START at perpendicular's far edge
            const collinearPartnerEnd = (wall1CollinearPartner || wall2CollinearPartner).end;
            const collinearTrimKey = collinearPartnerEnd === 'start' ? 'start' : 'end';

            // Determine which perpendicular edge is "entering" vs "exiting" the junction
            // based on the direction from host to collinear partner
            // For host wall: trim to the entering edge of perpendicular
            // For collinear partner: trim to the exiting edge of perpendicular
            // Use perp BODY edges (not finish edges) to compute host/collinear finish trims
            // Body edges are wider than finish edges, so the trim point is at the actual wall edge
            // This prevents the host finish line from extending into the perp wall's body
            if (nearFinishPt && perpLeft && perpRight) {
              const leftGapPt = lineIntersect(perpLeft, perpDir, nearFinishPt, hostDir);
              const rightGapPt = lineIntersect(perpRight, perpDir, nearFinishPt, hostDir);
              if (leftGapPt && rightGapPt) {
                // Order by t-value along host direction to find entering/exiting points
                const tLeft = (leftGapPt.x - nearFinishPt.x) * hostDir.x + (leftGapPt.y - nearFinishPt.y) * hostDir.y;
                const tRight = (rightGapPt.x - nearFinishPt.x) * hostDir.x + (rightGapPt.y - nearFinishPt.y) * hostDir.y;

                // For host wall: hostEnd tells us which end is at junction
                // If hostEnd='end', host approaches from negative t direction, so entering edge is the one with smaller t
                // If hostEnd='start', host approaches from positive t direction, so entering edge is the one with larger t
                const hostEnteringPt = hostEnd === 'end'
                  ? (tLeft < tRight ? leftGapPt : rightGapPt)
                  : (tLeft > tRight ? leftGapPt : rightGapPt);
                const collinearExitingPt = hostEnd === 'end'
                  ? (tLeft > tRight ? leftGapPt : rightGapPt)
                  : (tLeft < tRight ? leftGapPt : rightGapPt);

                // Set trim for host wall's NEAR side finish line
                if (side > 0) {
                  trims[hostWall.id][`${hostEnd}LeftFinishTrim`] = hostEnteringPt;
                  trims[collinearPartner.id][`${collinearTrimKey}LeftFinishTrim`] = collinearExitingPt;
                } else {
                  trims[hostWall.id][`${hostEnd}RightFinishTrim`] = hostEnteringPt;
                  trims[collinearPartner.id][`${collinearTrimKey}RightFinishTrim`] = collinearExitingPt;
                }
              }
            }
            // FAR side: also use perp BODY edges for wider, accurate trim
            if (farFinishPt && perpLeft && perpRight) {
              const leftGapPt = lineIntersect(perpLeft, perpDir, farFinishPt, hostDir);
              const rightGapPt = lineIntersect(perpRight, perpDir, farFinishPt, hostDir);
              if (leftGapPt && rightGapPt) {
                // Same logic for FAR side
                const tLeft = (leftGapPt.x - farFinishPt.x) * hostDir.x + (leftGapPt.y - farFinishPt.y) * hostDir.y;
                const tRight = (rightGapPt.x - farFinishPt.x) * hostDir.x + (rightGapPt.y - farFinishPt.y) * hostDir.y;

                const hostEnteringPt = hostEnd === 'end'
                  ? (tLeft < tRight ? leftGapPt : rightGapPt)
                  : (tLeft > tRight ? leftGapPt : rightGapPt);
                const collinearExitingPt = hostEnd === 'end'
                  ? (tLeft > tRight ? leftGapPt : rightGapPt)
                  : (tLeft < tRight ? leftGapPt : rightGapPt);

                // Set trim for host wall's FAR side finish line
                if (side > 0) {
                  trims[hostWall.id][`${hostEnd}RightFinishTrim`] = hostEnteringPt;
                  trims[collinearPartner.id][`${collinearTrimKey}RightFinishTrim`] = collinearExitingPt;
                } else {
                  trims[hostWall.id][`${hostEnd}LeftFinishTrim`] = hostEnteringPt;
                  trims[collinearPartner.id][`${collinearTrimKey}LeftFinishTrim`] = collinearExitingPt;
                }
              }
            }


            // Mark host and collinear partner as having collinear junction (suppress end caps)
            trims[hostWall.id][`${hostEnd}HasCollinear`] = true;
            trims[collinearPartner.id][`${collinearTrimKey}HasCollinear`] = true;

            // Don't process this as a normal L-corner
            continue;
          }

          const wall1IsExterior = geo1.isExterior;
          const wall2IsExterior = geo2.isExterior;
          const bothExterior = wall1IsExterior && wall2IsExterior;
          const bothInterior = !wall1IsExterior && !wall2IsExterior;

          if (bothExterior || bothInterior) {
            // Same wall type - apply full miter to both walls on both sides
            // But skip if trims already set (handles 3+ walls meeting at same point)
            if (!trims[wall1.id][`${trim1Key}LeftTrim`]) {
              trims[wall1.id][`${trim1Key}LeftTrim`] = intLL;
            } else if (DEBUG) {
            }
            if (!trims[wall2.id][`${trim2Key}LeftTrim`]) {
              trims[wall2.id][`${trim2Key}LeftTrim`] = intLL;
            } else if (DEBUG) {
            }
            if (!trims[wall1.id][`${trim1Key}RightTrim`]) {
              trims[wall1.id][`${trim1Key}RightTrim`] = intRR;
            } else if (DEBUG) {
            }
            if (!trims[wall2.id][`${trim2Key}RightTrim`]) {
              trims[wall2.id][`${trim2Key}RightTrim`] = intRR;
            } else if (DEBUG) {
            }
          } else {
            // Mixed wall types (exterior meets interior)
            // The exterior wall's OUTER edge (siding) should NOT be mitered
            // Only the interior-facing edges should be mitered
            //
            // For exterior walls (when not flipped):
            // - "left" edge (positive perpendicular) = outside (siding)
            // - "right" edge (negative perpendicular) = inside (drywall)
            // When flipped, these are swapped
            //
            // The interior wall should trim BOTH edges to the exterior wall's DRYWALL edge
            // (not to its own side's edge on the exterior wall)
            // The exterior wall should only miter its drywall edge

            // Determine which wall is exterior and which is interior
            const exteriorWall = wall1IsExterior ? wall1 : wall2;
            const interiorWall = wall1IsExterior ? wall2 : wall1;
            const exteriorGeo = wall1IsExterior ? geo1 : geo2;
            const interiorGeo = wall1IsExterior ? geo2 : geo1;
            const exteriorTrimKey = wall1IsExterior ? trim1Key : trim2Key;
            const interiorTrimKey = wall1IsExterior ? trim2Key : trim1Key;
            const exteriorDir = wall1IsExterior ? d1 : d2;
            const interiorDir = wall1IsExterior ? d2 : d1;
            const interiorLeft = wall1IsExterior ? w2Left : w1Left;
            const interiorRight = wall1IsExterior ? w2Right : w1Right;

            const exteriorFlipped = exteriorWall.flipped;
            // For unflipped exterior: right edge is drywall
            // For flipped exterior: left edge is drywall
            // Get a point on the exterior wall's drywall edge line
            const drywallEdgePt = wall1IsExterior
              ? (exteriorFlipped
                  ? (check.w1End === 'start' ? geo1.startLeft : geo1.endLeft)
                  : (check.w1End === 'start' ? geo1.startRight : geo1.endRight))
              : (exteriorFlipped
                  ? (check.w2End === 'start' ? geo2.startLeft : geo2.endLeft)
                  : (check.w2End === 'start' ? geo2.startRight : geo2.endRight));

            // Interior wall: BOTH edges trim to the exterior wall's DRYWALL edge line
            const intLeftToDrywall = lineIntersect(interiorLeft, interiorDir, drywallEdgePt, exteriorDir);
            const intRightToDrywall = lineIntersect(interiorRight, interiorDir, drywallEdgePt, exteriorDir);

            if (DEBUG) {
            }

            if (intLeftToDrywall) {
              trims[interiorWall.id][`${interiorTrimKey}LeftTrim`] = intLeftToDrywall;
            }
            if (intRightToDrywall) {
              trims[interiorWall.id][`${interiorTrimKey}RightTrim`] = intRightToDrywall;
            }

            // Exterior wall: only miter the drywall edge
            if (exteriorFlipped) {
              // Flipped: left is drywall - miter left
              trims[exteriorWall.id][`${exteriorTrimKey}LeftTrim`] = intLL;
            } else {
              // Not flipped: right is drywall - miter right
              trims[exteriorWall.id][`${exteriorTrimKey}RightTrim`] = intRR;
            }
          }
        }
        // If cross is near zero, walls are parallel/collinear - no miter needed
        // But we still need to suppress end caps where collinear walls meet end-to-end
        if (Math.abs(cross) <= 0.001) {
          // Collinear walls meeting end-to-end - suppress end caps at the junction
          trims[wall1.id][`${trim1Key}HasCollinear`] = true;
          trims[wall2.id][`${trim2Key}HasCollinear`] = true;
        }

        // Also compute finish line trims for L-corners
        const w1LeftFinish = check.w1End === 'start' ? geo1.startLeftFinish : geo1.endLeftFinish;
        const w1RightFinish = check.w1End === 'start' ? geo1.startRightFinish : geo1.endRightFinish;
        const w2LeftFinish = check.w2End === 'start' ? geo2.startLeftFinish : geo2.endLeftFinish;
        const w2RightFinish = check.w2End === 'start' ? geo2.startRightFinish : geo2.endRightFinish;

        // Compute ALL FOUR possible finish intersections
        // The correct pairing depends on wall directions at this specific corner
        const intFinishLL = (w1LeftFinish && w2LeftFinish) ? lineIntersect(w1LeftFinish, d1, w2LeftFinish, d2) : null;
        const intFinishRR = (w1RightFinish && w2RightFinish) ? lineIntersect(w1RightFinish, d1, w2RightFinish, d2) : null;
        const intFinishLR = (w1LeftFinish && w2RightFinish) ? lineIntersect(w1LeftFinish, d1, w2RightFinish, d2) : null;
        const intFinishRL = (w1RightFinish && w2LeftFinish) ? lineIntersect(w1RightFinish, d1, w2LeftFinish, d2) : null;

        // ============================================================================
        // CRITICAL L-CORNER FINISH LINE LOGIC
        // ============================================================================
        // For interior walls meeting at L-corners, BOTH finish lines need to be mitered.
        // The correct pairing depends on:
        // 1. Whether both walls have the same endpoint type (both START or both END)
        // 2. The turn direction at the corner (cross product sign)
        // XOR of these factors determines if we need LL+RR or LR+RL pairing.
        // ============================================================================
        if (Math.abs(cross) > 0.001) {
          const finishMaxDist = maxDist * 2;
          const isFinishReasonable = (pt) => pt && dist(pt, check.pt1) < finishMaxDist;

          // Determine which pairing is correct by checking which finish line endpoints
          // are on the same side of the corner. If w1.left and w2.left are closer together
          // than w1.left and w2.right, they're on the same side → use LL+RR.
          // Otherwise use LR+RL (cross pairing).
          const w1LP = w1LeftFinish || check.pt1;
          const w2LP = w2LeftFinish || check.pt1;
          const w2RP = w2RightFinish || check.pt1;
          const distLL = dist(w1LP, w2LP);
          const distLR = dist(w1LP, w2RP);
          const useCrossPairing = distLR < distLL;

          if (DEBUG) {
          }

          if (useCrossPairing) {
            // w1.left pairs with w2.right, w1.right pairs with w2.left
            if (intFinishLR && isFinishReasonable(intFinishLR)) {
              if (!trims[wall1.id][`${trim1Key}LeftFinishTrim`]) trims[wall1.id][`${trim1Key}LeftFinishTrim`] = intFinishLR;
              if (!trims[wall2.id][`${trim2Key}RightFinishTrim`]) trims[wall2.id][`${trim2Key}RightFinishTrim`] = intFinishLR;
            }
            if (intFinishRL && isFinishReasonable(intFinishRL)) {
              if (!trims[wall1.id][`${trim1Key}RightFinishTrim`]) trims[wall1.id][`${trim1Key}RightFinishTrim`] = intFinishRL;
              if (!trims[wall2.id][`${trim2Key}LeftFinishTrim`]) trims[wall2.id][`${trim2Key}LeftFinishTrim`] = intFinishRL;
            }
          } else {
            // w1.left pairs with w2.left, w1.right pairs with w2.right
            if (intFinishLL && isFinishReasonable(intFinishLL)) {
              if (!trims[wall1.id][`${trim1Key}LeftFinishTrim`]) trims[wall1.id][`${trim1Key}LeftFinishTrim`] = intFinishLL;
              if (!trims[wall2.id][`${trim2Key}LeftFinishTrim`]) trims[wall2.id][`${trim2Key}LeftFinishTrim`] = intFinishLL;
            }
            if (intFinishRR && isFinishReasonable(intFinishRR)) {
              if (!trims[wall1.id][`${trim1Key}RightFinishTrim`]) trims[wall1.id][`${trim1Key}RightFinishTrim`] = intFinishRR;
              if (!trims[wall2.id][`${trim2Key}RightFinishTrim`]) trims[wall2.id][`${trim2Key}RightFinishTrim`] = intFinishRR;
            }
          }

          if (DEBUG) {
            const w1L = trims[wall1.id][`${trim1Key}LeftFinishTrim`] ? 'SET' : 'MISSING';
            const w1R = trims[wall1.id][`${trim1Key}RightFinishTrim`] ? 'SET' : 'MISSING';
            const w2L = trims[wall2.id][`${trim2Key}LeftFinishTrim`] ? 'SET' : 'MISSING';
            const w2R = trims[wall2.id][`${trim2Key}RightFinishTrim`] ? 'SET' : 'MISSING';
          }
        }
      }

      // Check T-intersections: wall1's endpoint on wall2's body
      const tChecks = [
        { incoming: wall1, incomingEnd: 'start', host: wall2, incomingGeo: geo1, hostGeo: geo2 },
        { incoming: wall1, incomingEnd: 'end', host: wall2, incomingGeo: geo1, hostGeo: geo2 },
        { incoming: wall2, incomingEnd: 'start', host: wall1, incomingGeo: geo2, hostGeo: geo1 },
        { incoming: wall2, incomingEnd: 'end', host: wall1, incomingGeo: geo2, hostGeo: geo1 },
      ];

      for (const tCheck of tChecks) {
        const incomingPt = tCheck.incomingEnd === 'start' ? tCheck.incoming.start : tCheck.incoming.end;
        // Pass both wall thicknesses to allow detection when:
        // - incoming wall is snapped to the edge of the host wall rather than its centerline
        // - incoming wall centerline is outside host but wall bodies overlap
        const hostThickness = tCheck.hostGeo.thickness;
        const incomingThickness = tCheck.incomingGeo.thickness;
        const t = pointOnSegmentT(incomingPt, tCheck.host.start, tCheck.host.end, hostThickness, incomingThickness);

        // Debug: log T-junction detection attempts
        if (DEBUG_T_JUNCTION) {
          const hostLen = dist(tCheck.host.start, tCheck.host.end);
          const projT = ((incomingPt.x - tCheck.host.start.x) * (tCheck.host.end.x - tCheck.host.start.x) +
                        (incomingPt.y - tCheck.host.start.y) * (tCheck.host.end.y - tCheck.host.start.y)) / (hostLen * hostLen);
          const closestX = tCheck.host.start.x + projT * (tCheck.host.end.x - tCheck.host.start.x);
          const closestY = tCheck.host.start.y + projT * (tCheck.host.end.y - tCheck.host.start.y);
          const perpDist = dist(incomingPt, { x: closestX, y: closestY });
          const actualTolerance = Math.max((hostThickness + incomingThickness) / 2 + 15, 40);

          // Log ALL checks to understand the geometry
        }

        if (t === null) continue;

        // Found a T-intersection!
        // The host wall needs a gap in its near edge where the incoming wall meets
        // The incoming wall keeps its natural endpoints (no extension needed)

        const hostGeo = tCheck.hostGeo;
        const incomingGeo = tCheck.incomingGeo;

        // Determine which edge of the host wall the incoming wall ENTERS through
        // This is the "near" edge from the incoming wall's perspective
        //
        // The incoming wall is traveling TOWARD the host. We need to find which edge
        // of the host wall the incoming wall's JUNCTION POINT is closer to.
        // The junction point itself tells us which side we're approaching from.
        //
        // Calculate perpendicular distances from the junction point to each edge line
        // of the host wall. The closer edge is the one the incoming wall enters through.

        // Get a point on the host's left edge (use the point at the same t position as the junction)
        const hostLeftAtT = {
          x: hostGeo.startLeft.x + t * (hostGeo.endLeft.x - hostGeo.startLeft.x),
          y: hostGeo.startLeft.y + t * (hostGeo.endLeft.y - hostGeo.startLeft.y),
        };
        const hostRightAtT = {
          x: hostGeo.startRight.x + t * (hostGeo.endRight.x - hostGeo.startRight.x),
          y: hostGeo.startRight.y + t * (hostGeo.endRight.y - hostGeo.startRight.y),
        };

        // The junction point (incomingPt) is on or near the host's centerline
        // Measure distance from junction to each edge
        const distToLeftEdge = Math.sqrt(
          (incomingPt.x - hostLeftAtT.x) ** 2 +
          (incomingPt.y - hostLeftAtT.y) ** 2
        );
        const distToRightEdge = Math.sqrt(
          (incomingPt.x - hostRightAtT.x) ** 2 +
          (incomingPt.y - hostRightAtT.y) ** 2
        );

        // But wait - the junction point is at the centerline, so distances will be equal!
        // We need to check which side the incoming wall's CENTER (away from junction) is on.
        const incomingCenter = tCheck.incomingEnd === 'start'
          ? tCheck.incoming.end
          : tCheck.incoming.start;

        // Distance from incoming wall center to each edge
        const centerToLeftEdge = Math.sqrt(
          (incomingCenter.x - hostLeftAtT.x) ** 2 +
          (incomingCenter.y - hostLeftAtT.y) ** 2
        );
        const centerToRightEdge = Math.sqrt(
          (incomingCenter.x - hostRightAtT.x) ** 2 +
          (incomingCenter.y - hostRightAtT.y) ** 2
        );

        // The incoming wall is CLOSER to one edge - that's the NEAR edge (where it enters)
        // But we want to trim the incoming wall to that near edge and put the gap there
        const nearEdgeIsLeft = centerToLeftEdge < centerToRightEdge;

        if (DEBUG_T_JUNCTION) {
        }

        const side = nearEdgeIsLeft ? 1 : -1;

        // Calculate gap positions along the host wall
        // Instead of using centerline-based approximation, calculate where the incoming
        // wall's edges actually intersect the host wall's edge for precise gap placement
        const incomingHalfThick = incomingGeo.halfThick;
        const hostLen = hostGeo.len;

        // Direction the incoming wall is traveling (toward host)
        const incomingDirToHost = tCheck.incomingEnd === 'start'
          ? { x: -incomingGeo.dirX, y: -incomingGeo.dirY }
          : { x: incomingGeo.dirX, y: incomingGeo.dirY };

        const hostEdgeDir = { x: hostGeo.dirX, y: hostGeo.dirY };

        // Get the incoming wall's edge endpoints at the junction
        const incomingLeft = tCheck.incomingEnd === 'start' ? incomingGeo.startLeft : incomingGeo.endLeft;
        const incomingRight = tCheck.incomingEnd === 'start' ? incomingGeo.startRight : incomingGeo.endRight;

        // Helper to calculate t position along host edge
        const calcTOnHost = (point, hostStart, hostEnd) => {
          const dx = hostEnd.x - hostStart.x;
          const dy = hostEnd.y - hostStart.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len === 0) return 0;
          return ((point.x - hostStart.x) * dx + (point.y - hostStart.y) * dy) / (len * len);
        };

        // Default gap based on centerline (fallback)
        const tGapHalf = incomingHalfThick / hostLen;
        let tGapStart = Math.max(0, t - tGapHalf);
        let tGapEnd = Math.min(1, t + tGapHalf);

        // The incoming wall is on one side of the host wall
        // Gap goes in the edge facing the incoming wall
        // Incoming wall edges need to extend to meet that edge

        const trimKey = tCheck.incomingEnd === 'start' ? 'start' : 'end';

        // Also get the FAR edge endpoints (the other end of the incoming wall, away from junction)
        const incomingLeftFar = tCheck.incomingEnd === 'start' ? incomingGeo.endLeft : incomingGeo.startLeft;
        const incomingRightFar = tCheck.incomingEnd === 'start' ? incomingGeo.endRight : incomingGeo.startRight;

        // The incoming wall approaches from one side of the host wall
        // - Gap goes in the host wall's edge facing the incoming wall
        // - Incoming wall edges should be trimmed to that SAME edge (where they meet)
        //
        // HOWEVER, visually we want the incoming wall to stop at the NEAR edge of the host
        // (the edge closest to the incoming wall), not pass through to the far edge.
        // The "side" calculation tells us which edge the incoming wall is approaching FROM.

        // Determine if the host wall is exterior and which side is "outside" (siding)
        // For exterior walls: left edge is outside (siding), right edge is inside (drywall)
        // If flipped, it's reversed
        const hostIsExterior = hostGeo.isExterior;
        const hostFlipped = tCheck.host.flipped === true; // Explicitly check for true, not just truthy
        // For unflipped exterior: left = outside (siding), right = inside (drywall)
        // For flipped exterior: left = inside (drywall), right = outside (siding)
        const hostInsideIsRight = hostIsExterior && !hostFlipped;  // drywall on right
        const hostInsideIsLeft = hostIsExterior && hostFlipped;    // drywall on left (flipped)

        // For exterior walls: interior walls should ONLY connect to the INSIDE (drywall) edge
        // The siding edge should NEVER get a gap - it continues unbroken
        // The incoming wall trims to the inside edge of the exterior wall


        if (hostIsExterior) {
          // Exterior host wall - special handling:
          // 1. GAP only goes in the edge on the SAME side as the incoming wall (the "near" edge)
          // 2. BUT only if that edge is the drywall (inside) edge - NEVER put gap in siding
          // 3. TRIM incoming wall to the NEAR edge (whichever edge it approaches from)
          //
          // For unflipped exterior: left = siding (outside), right = drywall (inside)
          // For flipped exterior: left = drywall (inside), right = siding (outside)

          const approachingFromRight = side < 0; // side < 0 means incoming is on RIGHT side
          const approachingFromLeft = side > 0;  // side > 0 means incoming is on LEFT side

          // Determine if the incoming wall is approaching from the drywall side
          // If unflipped: drywall is on right, so approaching from right = drywall side
          // If flipped: drywall is on left, so approaching from left = drywall side
          const drywallIsRight = !hostFlipped;
          const drywallIsLeft = hostFlipped;
          const approachingFromDrywallSide = (drywallIsRight && approachingFromRight) || (drywallIsLeft && approachingFromLeft);


          // Trim incoming wall to the NEAR edge (the edge it approaches from)
          // For a clean T-junction, we want the incoming wall to end with a perpendicular cut
          // First, find where the incoming wall's CENTERLINE hits the host edge
          const nearEdgePt = side > 0 ? hostGeo.startLeft : hostGeo.startRight;
          const incomingCenterAtJunction = tCheck.incomingEnd === 'start'
            ? tCheck.incoming.start
            : tCheck.incoming.end;

          // Find where centerline intersects the host edge
          const centerTrim = lineIntersect(incomingCenterAtJunction, incomingDirToHost, nearEdgePt, hostEdgeDir);

          // Now create perpendicular trim points for left and right edges at this centerline position
          // The trim points should be at the same distance along the wall, creating a perpendicular cut
          let leftTrim, rightTrim;
          if (centerTrim) {
            // Calculate the perpendicular offset from centerline to each edge
            leftTrim = {
              x: centerTrim.x + incomingGeo.perpX * incomingGeo.halfThick,
              y: centerTrim.y + incomingGeo.perpY * incomingGeo.halfThick
            };
            rightTrim = {
              x: centerTrim.x - incomingGeo.perpX * incomingGeo.halfThick,
              y: centerTrim.y - incomingGeo.perpY * incomingGeo.halfThick
            };
          } else {
            // Fallback to diagonal intersection if centerline calc fails
            leftTrim = lineIntersect(incomingLeft, incomingDirToHost, nearEdgePt, hostEdgeDir);
            rightTrim = lineIntersect(incomingRight, incomingDirToHost, nearEdgePt, hostEdgeDir);
          }

          // For T-junctions, ALWAYS create gap on the NEAR edge (the side the incoming wall approaches from)
          // The "no gap on siding" logic only applies to L-corners where exterior walls meet
          // Project the perpendicular trim points onto the host edge line for the gap
          if (leftTrim && rightTrim) {
            // The trim points form a perpendicular cut but may not be exactly on the host edge
            // Project them onto the host edge to get accurate gap points
            // Use the NEAR edge (based on side) not the drywall edge
            const hostEdgeStart = side > 0 ? hostGeo.startLeft : hostGeo.startRight;
            const hostEdgeEnd = side > 0 ? hostGeo.endLeft : hostGeo.endRight;

            // Project leftTrim onto host edge
            const leftGapPt = lineIntersect(leftTrim, { x: hostGeo.perpX, y: hostGeo.perpY }, hostEdgeStart, hostEdgeDir);
            // Project rightTrim onto host edge
            const rightGapPt = lineIntersect(rightTrim, { x: hostGeo.perpX, y: hostGeo.perpY }, hostEdgeStart, hostEdgeDir);

            if (leftGapPt && rightGapPt) {
              const leftT = calcTOnHost(leftGapPt, hostEdgeStart, hostEdgeEnd);
              const rightT = calcTOnHost(rightGapPt, hostEdgeStart, hostEdgeEnd);
              const gapStartPt = leftT < rightT ? leftGapPt : rightGapPt;
              const gapEndPt = leftT < rightT ? rightGapPt : leftGapPt;
              const actualTStart = Math.min(leftT, rightT);
              const actualTEnd = Math.max(leftT, rightT);

              if (side > 0) {
                trims[tCheck.host.id].leftEdgeGaps.push({ start: gapStartPt, end: gapEndPt, t: t, tStart: actualTStart, tEnd: actualTEnd });
              } else {
                trims[tCheck.host.id].rightEdgeGaps.push({ start: gapStartPt, end: gapEndPt, t: t, tStart: actualTStart, tEnd: actualTEnd });
              }
            } else {
            }
          } else {
          }

          // Only apply trim if it SHORTENS the wall
          const leftFarDist = leftTrim ? Math.sqrt((incomingLeftFar.x - leftTrim.x)**2 + (incomingLeftFar.y - leftTrim.y)**2) : Infinity;
          const leftOrigDist = Math.sqrt((incomingLeftFar.x - incomingLeft.x)**2 + (incomingLeftFar.y - incomingLeft.y)**2);
          const rightFarDist = rightTrim ? Math.sqrt((incomingRightFar.x - rightTrim.x)**2 + (incomingRightFar.y - rightTrim.y)**2) : Infinity;
          const rightOrigDist = Math.sqrt((incomingRightFar.x - incomingRight.x)**2 + (incomingRightFar.y - incomingRight.y)**2);


          if (leftTrim && leftFarDist < leftOrigDist) {
            trims[tCheck.incoming.id][`${trimKey}LeftTrim`] = leftTrim;
          }
          if (rightTrim && rightFarDist < rightOrigDist) {
            trims[tCheck.incoming.id][`${trimKey}RightTrim`] = rightTrim;
          }

        } else {
          // Non-exterior host wall - use normal logic based on which side incoming is on

          // For clean T-junction, find where centerline hits host edge, then create perpendicular cut
          const nearEdgePtNonExt = side > 0 ? hostGeo.startLeft : hostGeo.startRight;
          const incomingCenterAtJunctionNonExt = tCheck.incomingEnd === 'start'
            ? tCheck.incoming.start
            : tCheck.incoming.end;
          const centerTrimNonExt = lineIntersect(incomingCenterAtJunctionNonExt, incomingDirToHost, nearEdgePtNonExt, hostEdgeDir);

          let leftTrim, rightTrim;
          if (centerTrimNonExt) {
            // Create perpendicular trim points
            leftTrim = {
              x: centerTrimNonExt.x + incomingGeo.perpX * incomingGeo.halfThick,
              y: centerTrimNonExt.y + incomingGeo.perpY * incomingGeo.halfThick
            };
            rightTrim = {
              x: centerTrimNonExt.x - incomingGeo.perpX * incomingGeo.halfThick,
              y: centerTrimNonExt.y - incomingGeo.perpY * incomingGeo.halfThick
            };
          } else {
            // Fallback
            leftTrim = lineIntersect(incomingLeft, incomingDirToHost, nearEdgePtNonExt, hostEdgeDir);
            rightTrim = lineIntersect(incomingRight, incomingDirToHost, nearEdgePtNonExt, hostEdgeDir);
          }

          if (side > 0) {
            // Incoming wall body is on the "left" side - gap in left edge
            // Project perpendicular trim points onto host edge for accurate gap
            if (leftTrim && rightTrim) {
              const leftGapPt = lineIntersect(leftTrim, { x: hostGeo.perpX, y: hostGeo.perpY }, hostGeo.startLeft, hostEdgeDir);
              const rightGapPt = lineIntersect(rightTrim, { x: hostGeo.perpX, y: hostGeo.perpY }, hostGeo.startLeft, hostEdgeDir);
              if (leftGapPt && rightGapPt) {
                const leftT = calcTOnHost(leftGapPt, hostGeo.startLeft, hostGeo.endLeft);
                const rightT = calcTOnHost(rightGapPt, hostGeo.startLeft, hostGeo.endLeft);
                const gapStartPt = leftT < rightT ? leftGapPt : rightGapPt;
                const gapEndPt = leftT < rightT ? rightGapPt : leftGapPt;
                const actualTStart = Math.min(leftT, rightT);
                const actualTEnd = Math.max(leftT, rightT);
                trims[tCheck.host.id].leftEdgeGaps.push({ start: gapStartPt, end: gapEndPt, t: t, tStart: actualTStart, tEnd: actualTEnd });

              }
            }

            if (leftTrim) {
              trims[tCheck.incoming.id][`${trimKey}LeftTrim`] = leftTrim;
            }
            if (rightTrim) {
              trims[tCheck.incoming.id][`${trimKey}RightTrim`] = rightTrim;
            }
          } else {
            // Incoming wall body is on the "right" side - gap in right edge
            // Project perpendicular trim points onto host edge for accurate gap
            if (leftTrim && rightTrim) {
              const leftGapPt = lineIntersect(leftTrim, { x: hostGeo.perpX, y: hostGeo.perpY }, hostGeo.startRight, hostEdgeDir);
              const rightGapPt = lineIntersect(rightTrim, { x: hostGeo.perpX, y: hostGeo.perpY }, hostGeo.startRight, hostEdgeDir);
              if (leftGapPt && rightGapPt) {
                const leftT = calcTOnHost(leftGapPt, hostGeo.startRight, hostGeo.endRight);
                const rightT = calcTOnHost(rightGapPt, hostGeo.startRight, hostGeo.endRight);
                const gapStartPt = leftT < rightT ? leftGapPt : rightGapPt;
                const gapEndPt = leftT < rightT ? rightGapPt : leftGapPt;
                const actualTStart = Math.min(leftT, rightT);
                const actualTEnd = Math.max(leftT, rightT);
                trims[tCheck.host.id].rightEdgeGaps.push({ start: gapStartPt, end: gapEndPt, t: t, tStart: actualTStart, tEnd: actualTEnd });

              }
            }

            if (leftTrim) {
              trims[tCheck.incoming.id][`${trimKey}LeftTrim`] = leftTrim;
            }
            if (rightTrim) {
              trims[tCheck.incoming.id][`${trimKey}RightTrim`] = rightTrim;
            }
          }
        }

        // Mark the incoming wall's end so we don't draw an end cap there
        trims[tCheck.incoming.id][`${trimKey}HasT`] = true;
        // Also store if the host was exterior (affects how finish-to-main connectors are drawn)
        trims[tCheck.incoming.id][`${trimKey}HostIsExterior`] = hostIsExterior;


        // Also handle finish line gaps and trims for T-intersections
        // Incoming wall's finish lines need to be trimmed to match where the main wall edges are trimmed
        // For exterior host walls: ALWAYS trim to drywall edge and create gap there
        // For non-exterior host walls: use side to determine which edge
        const incomingLeftFinish = tCheck.incomingEnd === 'start' ? incomingGeo.startLeftFinish : incomingGeo.endLeftFinish;
        const incomingRightFinish = tCheck.incomingEnd === 'start' ? incomingGeo.startRightFinish : incomingGeo.endRightFinish;


        // Also get the FAR finish endpoints (the other end of the incoming wall, away from junction)
        const incomingLeftFinishFar = tCheck.incomingEnd === 'start' ? incomingGeo.endLeftFinish : incomingGeo.startLeftFinish;
        const incomingRightFinishFar = tCheck.incomingEnd === 'start' ? incomingGeo.endRightFinish : incomingGeo.startRightFinish;

        // Determine which side of the HOST wall gets a finish line gap
        // For EXTERIOR host walls: gap goes in DRYWALL side (interior side of exterior wall)
        // For INTERIOR host walls: gap goes on the NEAR side (where incoming wall approaches)
        let finishGapInRight;
        let createFinishGap = true;

        if (hostIsExterior) {
          // Exterior wall: gap goes in drywall side (interior finish line)
          // For unflipped exterior: drywall is on RIGHT
          // For flipped exterior: drywall is on LEFT
          const drywallIsOnRight = !hostFlipped;
          finishGapInRight = drywallIsOnRight;
        } else {
          // Interior wall: gap goes on the side the incoming wall approaches from
          finishGapInRight = side < 0; // side < 0 means approaching from right
        }

        // For T-junctions, the finish line should be trimmed to a point that is:
        // - On the same perpendicular line as the main edge trim point
        // - On the finish line itself
        // This ensures the connection from finish trim to main trim is perpendicular to the wall
        let leftFinishTrim = null;
        let rightFinishTrim = null;

        // Get the main edge trim that was just calculated for this T-junction
        const mainLeftTrim = trims[tCheck.incoming.id][`${trimKey}LeftTrim`];
        const mainRightTrim = trims[tCheck.incoming.id][`${trimKey}RightTrim`];


        // Calculate finish trim points for T-junctions
        // For EXTERIOR host walls: incoming finish lines should extend to host's DRYWALL line (interior)
        // For INTERIOR host walls: finish lines trim at same position as main edges

        // Get the target line for finish trim - for exterior hosts, use the drywall (interior) line
        let finishTargetLine = null;
        let finishTargetDir = hostEdgeDir;

        if (hostIsExterior) {
          // Exterior host: incoming finish lines should reach the host's drywall line
          const drywallIsOnRight = !hostFlipped;
          finishTargetLine = drywallIsOnRight ? hostGeo.startRightFinish : hostGeo.startLeftFinish;
        }

        // Handle finish line trims and gaps
        const incomingDir = { x: incomingGeo.dirX, y: incomingGeo.dirY };

        if (hostIsExterior) {
          // EXTERIOR HOST: trim incoming finish lines to host's drywall line
          // Skip if already handled by 3-way junction code
          if (finishTargetLine && !trims[tCheck.incoming.id].is3WayJunction) {
            if (incomingLeftFinish) {
              leftFinishTrim = lineIntersect(incomingLeftFinish, incomingDir, finishTargetLine, hostEdgeDir);
              if (leftFinishTrim) trims[tCheck.incoming.id][`${trimKey}LeftFinishTrim`] = leftFinishTrim;
            }
            if (incomingRightFinish) {
              rightFinishTrim = lineIntersect(incomingRightFinish, incomingDir, finishTargetLine, hostEdgeDir);
              if (rightFinishTrim) trims[tCheck.incoming.id][`${trimKey}RightFinishTrim`] = rightFinishTrim;
            }
          }

          // Create gap in host's drywall finish line
          const drywallIsOnRight = !hostFlipped;
          const hostFinishForGap = drywallIsOnRight ? hostGeo.startRightFinish : hostGeo.startLeftFinish;
          const gapsArray = drywallIsOnRight ? trims[tCheck.host.id].rightFinishGaps : trims[tCheck.host.id].leftFinishGaps;

          if (hostFinishForGap && leftFinishTrim && rightFinishTrim) {
            const tLeft = (leftFinishTrim.x - hostFinishForGap.x) * hostGeo.dirX +
                          (leftFinishTrim.y - hostFinishForGap.y) * hostGeo.dirY;
            const tRight = (rightFinishTrim.x - hostFinishForGap.x) * hostGeo.dirX +
                           (rightFinishTrim.y - hostFinishForGap.y) * hostGeo.dirY;

            const gapStart = tLeft < tRight ? leftFinishTrim : rightFinishTrim;
            const gapEnd = tLeft < tRight ? rightFinishTrim : leftFinishTrim;

            gapsArray.push({ start: gapStart, end: gapEnd, t: t, tStart: tGapStart, tEnd: tGapEnd });
          }
        } else {
          // INTERIOR HOST
          const incomingIsExterior = incomingGeo.isExterior;

          if (!incomingIsExterior) {
            // INTERIOR-TO-INTERIOR T-junction:
            // For interior walls, both finish lines (left and right) exist on both sides
            // The incoming wall's finish lines should stop at the host's FAR finish line
            // (the one on the opposite side from where the incoming wall enters)
            // EXCEPTION: If the incoming wall has a collinear partner at the junction,
            // trim to the NEAR finish instead (the partner handles the other side)
            // Create gaps in BOTH host finish lines where the incoming wall passes through

            // NEAR finish is on the side the incoming wall approaches from
            // FAR finish is on the opposite side (where incoming wall exits the host)
            const nearFinishPt = side > 0 ? hostGeo.startLeftFinish : hostGeo.startRightFinish;
            const farFinishPt = side > 0 ? hostGeo.startRightFinish : hostGeo.startLeftFinish;
            const nearGapsArray = side > 0 ? trims[tCheck.host.id].leftFinishGaps : trims[tCheck.host.id].rightFinishGaps;
            const farGapsArray = side > 0 ? trims[tCheck.host.id].rightFinishGaps : trims[tCheck.host.id].leftFinishGaps;

            // Check if the incoming wall has a collinear partner at this junction
            // If so, the finish should only extend to the host's NEAR BODY EDGE (not through to far)
            // because the collinear partner handles the other side
            const incomingDir2 = { x: incomingGeo.dirX, y: incomingGeo.dirY };
            const incomingCollinear = findCollinearWallAtJunction(walls, tCheck.incoming, tCheck.incomingEnd, incomingPt, incomingDir2, geos);
            // With collinear partner: trim to near body edge (same as wall body trim)
            // Without: trim to far finish (extend through to close cavity)
            const nearBodyEdgePt = side > 0 ? hostGeo.startLeft : hostGeo.startRight;
            const finishTarget = incomingCollinear ? nearBodyEdgePt : farFinishPt;


            // Trim incoming finish lines to the target finish line
            // Skip if already handled by 3-way junction code
            if (finishTarget && !trims[tCheck.incoming.id].is3WayJunction) {
              if (incomingLeftFinish) {
                leftFinishTrim = lineIntersect(incomingLeftFinish, incomingDir, finishTarget, hostEdgeDir);
                if (leftFinishTrim) trims[tCheck.incoming.id][`${trimKey}LeftFinishTrim`] = leftFinishTrim;
              }
              if (incomingRightFinish) {
                rightFinishTrim = lineIntersect(incomingRightFinish, incomingDir, finishTarget, hostEdgeDir);
                if (rightFinishTrim) trims[tCheck.incoming.id][`${trimKey}RightFinishTrim`] = rightFinishTrim;
              }
            } else if (trims[tCheck.incoming.id].is3WayJunction) {
            }

            // Create gaps in host finish lines using incoming wall's BODY edges
            // (body is wider than finish, ensures gap covers full wall width)
            // Gap in NEAR finish line (where incoming enters)
            if (nearFinishPt) {
              const nearLeftInt = lineIntersect(incomingLeft, incomingDirToHost, nearFinishPt, hostEdgeDir);
              const nearRightInt = lineIntersect(incomingRight, incomingDirToHost, nearFinishPt, hostEdgeDir);
              if (nearLeftInt && nearRightInt) {
                const tLeft = (nearLeftInt.x - nearFinishPt.x) * hostGeo.dirX + (nearLeftInt.y - nearFinishPt.y) * hostGeo.dirY;
                const tRight = (nearRightInt.x - nearFinishPt.x) * hostGeo.dirX + (nearRightInt.y - nearFinishPt.y) * hostGeo.dirY;
                const gapStart = tLeft < tRight ? nearLeftInt : nearRightInt;
                const gapEnd = tLeft < tRight ? nearRightInt : nearLeftInt;
                nearGapsArray.push({ start: gapStart, end: gapEnd, t: t, tStart: tGapStart, tEnd: tGapEnd });

                // Add connector line to close the gap at the junction
                // Skip for collinear walls (finish trimmed to body edge, no connector needed)
                if (!trims[tCheck.incoming.id].is3WayJunction && !incomingCollinear && incomingLeftFinish && incomingRightFinish) {
                  const connLeft = lineIntersect(incomingLeftFinish, incomingDir, nearFinishPt, hostEdgeDir);
                  const connRight = lineIntersect(incomingRightFinish, incomingDir, nearFinishPt, hostEdgeDir);
                  if (connLeft && connRight) {
                    trims[tCheck.incoming.id].interiorTConnectors.push({ from: connLeft, to: connRight });
                  }
                }
              }
            }

            // Gap in FAR finish line (where incoming exits) - only when no collinear partner
            if (!incomingCollinear && farFinishPt) {
              const farLeftInt = lineIntersect(incomingLeft, incomingDirToHost, farFinishPt, hostEdgeDir);
              const farRightInt = lineIntersect(incomingRight, incomingDirToHost, farFinishPt, hostEdgeDir);
              if (farLeftInt && farRightInt) {
                const tLeft = (farLeftInt.x - farFinishPt.x) * hostGeo.dirX + (farLeftInt.y - farFinishPt.y) * hostGeo.dirY;
                const tRight = (farRightInt.x - farFinishPt.x) * hostGeo.dirX + (farRightInt.y - farFinishPt.y) * hostGeo.dirY;
                const gapStart = tLeft < tRight ? farLeftInt : farRightInt;
                const gapEnd = tLeft < tRight ? farRightInt : farLeftInt;
                farGapsArray.push({ start: gapStart, end: gapEnd, t: t, tStart: tGapStart, tEnd: tGapEnd });
              }
            }
          } else {
            // EXTERIOR incoming to INTERIOR host:
            // The exterior wall's finish lines (siding + drywall) should stop at the host's FAR finish line
            // Create gaps in BOTH host finish lines where the exterior wall passes through

            // NEAR finish is on the side the incoming wall approaches from
            // FAR finish is on the opposite side (where incoming wall exits the host)
            const nearFinishPt = side > 0 ? hostGeo.startLeftFinish : hostGeo.startRightFinish;
            const farFinishPt = side > 0 ? hostGeo.startRightFinish : hostGeo.startLeftFinish;
            const nearGapsArray = side > 0 ? trims[tCheck.host.id].leftFinishGaps : trims[tCheck.host.id].rightFinishGaps;
            const farGapsArray = side > 0 ? trims[tCheck.host.id].rightFinishGaps : trims[tCheck.host.id].leftFinishGaps;

            // Check if the incoming wall has a collinear partner at this junction
            const incomingDir3 = { x: incomingGeo.dirX, y: incomingGeo.dirY };
            const incomingCollinear2 = findCollinearWallAtJunction(walls, tCheck.incoming, tCheck.incomingEnd, incomingPt, incomingDir3, geos);
            const nearBodyEdgePt2 = side > 0 ? hostGeo.startLeft : hostGeo.startRight;
            const finishTarget2 = incomingCollinear2 ? nearBodyEdgePt2 : farFinishPt;

            // Trim incoming finish lines to the target finish line
            // With collinear partner: NEAR BODY EDGE (partner handles the other side)
            // Without: FAR finish (extend through to close cavity)
            // Skip if already handled by 3-way junction code
            if (finishTarget2 && !trims[tCheck.incoming.id].is3WayJunction) {
              if (incomingLeftFinish) {
                leftFinishTrim = lineIntersect(incomingLeftFinish, incomingDir, finishTarget2, hostEdgeDir);
                if (leftFinishTrim) trims[tCheck.incoming.id][`${trimKey}LeftFinishTrim`] = leftFinishTrim;
              }
              if (incomingRightFinish) {
                rightFinishTrim = lineIntersect(incomingRightFinish, incomingDir, finishTarget2, hostEdgeDir);
                if (rightFinishTrim) trims[tCheck.incoming.id][`${trimKey}RightFinishTrim`] = rightFinishTrim;
              }
            }

            // Create gaps in host finish lines
            // Gap in NEAR finish line (where incoming enters)
            if (nearFinishPt && incomingLeftFinish && incomingRightFinish) {
              const nearLeftInt = lineIntersect(incomingLeftFinish, incomingDir, nearFinishPt, hostEdgeDir);
              const nearRightInt = lineIntersect(incomingRightFinish, incomingDir, nearFinishPt, hostEdgeDir);
              if (nearLeftInt && nearRightInt) {
                const tLeft = (nearLeftInt.x - nearFinishPt.x) * hostGeo.dirX + (nearLeftInt.y - nearFinishPt.y) * hostGeo.dirY;
                const tRight = (nearRightInt.x - nearFinishPt.x) * hostGeo.dirX + (nearRightInt.y - nearFinishPt.y) * hostGeo.dirY;
                const gapStart = tLeft < tRight ? nearLeftInt : nearRightInt;
                const gapEnd = tLeft < tRight ? nearRightInt : nearLeftInt;
                nearGapsArray.push({ start: gapStart, end: gapEnd, t: t, tStart: tGapStart, tEnd: tGapEnd });

                // Add connector line at the NEAR finish line to close off the gap
                // Skip for 3-way junctions (perpendicular passes through, doesn't stop)
                if (!trims[tCheck.incoming.id].is3WayJunction) {
                  trims[tCheck.incoming.id].interiorTConnectors.push({
                    from: nearLeftInt,
                    to: nearRightInt
                  });
                }
              }
            }

            // Gap in FAR finish line (where incoming exits) - only if finish extends to far side
            if (!incomingCollinear2 && farFinishPt && incomingLeftFinish && incomingRightFinish) {
              const farLeftInt = lineIntersect(incomingLeftFinish, incomingDir, farFinishPt, hostEdgeDir);
              const farRightInt = lineIntersect(incomingRightFinish, incomingDir, farFinishPt, hostEdgeDir);
              if (farLeftInt && farRightInt) {
                const tLeft = (farLeftInt.x - farFinishPt.x) * hostGeo.dirX + (farLeftInt.y - farFinishPt.y) * hostGeo.dirY;
                const tRight = (farRightInt.x - farFinishPt.x) * hostGeo.dirX + (farRightInt.y - farFinishPt.y) * hostGeo.dirY;
                const gapStart = tLeft < tRight ? farLeftInt : farRightInt;
                const gapEnd = tLeft < tRight ? farRightInt : farLeftInt;
                farGapsArray.push({ start: gapStart, end: gapEnd, t: t, tStart: tGapStart, tEnd: tGapEnd });
              }
            }
          }
        }
      }

      // ============================================================================
      // CROSS INTERSECTION DETECTION
      // ============================================================================
      // Check if two walls' bodies cross each other (neither endpoint at intersection)
      // This happens when walls pass through each other mid-span
      // ============================================================================

      // Check if wall centerlines intersect
      // Find intersection point of centerlines
      const centerIntersect = lineIntersect(wall1.start, { x: geo1.dirX, y: geo1.dirY }, wall2.start, { x: geo2.dirX, y: geo2.dirY });

      if (centerIntersect) {
        // Check if intersection is within BOTH wall segments (not at endpoints)
        const t1 = ((centerIntersect.x - wall1.start.x) * geo1.dirX + (centerIntersect.y - wall1.start.y) * geo1.dirY) / (geo1.len);
        const t2 = ((centerIntersect.x - wall2.start.x) * geo2.dirX + (centerIntersect.y - wall2.start.y) * geo2.dirY) / (geo2.len);

        // Margin to exclude endpoints (avoid double-processing with T-junctions)
        const endpointMargin = 0.05;

        if (t1 > endpointMargin && t1 < (1 - endpointMargin) && t2 > endpointMargin && t2 < (1 - endpointMargin)) {
          // This is a TRUE cross intersection - both walls pass through each other

          // For cross intersections, we need to create gaps in BOTH walls' finish lines
          // Each wall gets gaps where the other wall passes through

          // For cross intersections: use FINISH lines to cut FINISH lines
          // This ensures gaps are at the actual crossing points of the finish lines
          const createCrossGaps = (hostWall, hostGeo, crossingGeo, hostTrims) => {
            const crossingDir = { x: crossingGeo.dirX, y: crossingGeo.dirY };
            const hostDir = { x: hostGeo.dirX, y: hostGeo.dirY };

            // Use the crossing wall's FINISH lines to define where to cut the host's finish lines
            const crossLeftFinish = crossingGeo.startLeftFinish || crossingGeo.startLeft;
            const crossRightFinish = crossingGeo.startRightFinish || crossingGeo.startRight;

            // Create gaps in host's left finish line
            if (hostGeo.startLeftFinish && crossLeftFinish && crossRightFinish) {
              const gapLeft = lineIntersect(crossLeftFinish, crossingDir, hostGeo.startLeftFinish, hostDir);
              const gapRight = lineIntersect(crossRightFinish, crossingDir, hostGeo.startLeftFinish, hostDir);

              if (gapLeft && gapRight) {
                const tLeft = (gapLeft.x - hostGeo.startLeftFinish.x) * hostGeo.dirX + (gapLeft.y - hostGeo.startLeftFinish.y) * hostGeo.dirY;
                const tRight = (gapRight.x - hostGeo.startLeftFinish.x) * hostGeo.dirX + (gapRight.y - hostGeo.startLeftFinish.y) * hostGeo.dirY;
                const gapStart = tLeft < tRight ? gapLeft : gapRight;
                const gapEnd = tLeft < tRight ? gapRight : gapLeft;
                hostTrims.leftFinishGaps.push({ start: gapStart, end: gapEnd });
              }
            }

            // Create gaps in host's right finish line
            if (hostGeo.startRightFinish && crossLeftFinish && crossRightFinish) {
              const gapLeft = lineIntersect(crossLeftFinish, crossingDir, hostGeo.startRightFinish, hostDir);
              const gapRight = lineIntersect(crossRightFinish, crossingDir, hostGeo.startRightFinish, hostDir);

              if (gapLeft && gapRight) {
                const tLeft = (gapLeft.x - hostGeo.startRightFinish.x) * hostGeo.dirX + (gapLeft.y - hostGeo.startRightFinish.y) * hostGeo.dirY;
                const tRight = (gapRight.x - hostGeo.startRightFinish.x) * hostGeo.dirX + (gapRight.y - hostGeo.startRightFinish.y) * hostGeo.dirY;
                const gapStart = tLeft < tRight ? gapLeft : gapRight;
                const gapEnd = tLeft < tRight ? gapRight : gapLeft;
                hostTrims.rightFinishGaps.push({ start: gapStart, end: gapEnd });
              }
            }

            // Also create gaps in host's EDGE lines where crossing wall passes through
            const crossLeftEdge = crossingGeo.startLeft;
            const crossRightEdge = crossingGeo.startRight;

            // Create gaps in host's left edge line
            if (hostGeo.startLeft && crossLeftEdge && crossRightEdge) {
              const gapLeft = lineIntersect(crossLeftEdge, crossingDir, hostGeo.startLeft, hostDir);
              const gapRight = lineIntersect(crossRightEdge, crossingDir, hostGeo.startLeft, hostDir);

              if (gapLeft && gapRight) {
                const tLeft = (gapLeft.x - hostGeo.startLeft.x) * hostGeo.dirX + (gapLeft.y - hostGeo.startLeft.y) * hostGeo.dirY;
                const tRight = (gapRight.x - hostGeo.startLeft.x) * hostGeo.dirX + (gapRight.y - hostGeo.startLeft.y) * hostGeo.dirY;
                const gapStart = tLeft < tRight ? gapLeft : gapRight;
                const gapEnd = tLeft < tRight ? gapRight : gapLeft;
                hostTrims.leftEdgeGaps.push({ start: gapStart, end: gapEnd });
              }
            }

            // Create gaps in host's right edge line
            if (hostGeo.startRight && crossLeftEdge && crossRightEdge) {
              const gapLeft = lineIntersect(crossLeftEdge, crossingDir, hostGeo.startRight, hostDir);
              const gapRight = lineIntersect(crossRightEdge, crossingDir, hostGeo.startRight, hostDir);

              if (gapLeft && gapRight) {
                const tLeft = (gapLeft.x - hostGeo.startRight.x) * hostGeo.dirX + (gapLeft.y - hostGeo.startRight.y) * hostGeo.dirY;
                const tRight = (gapRight.x - hostGeo.startRight.x) * hostGeo.dirX + (gapRight.y - hostGeo.startRight.y) * hostGeo.dirY;
                const gapStart = tLeft < tRight ? gapLeft : gapRight;
                const gapEnd = tLeft < tRight ? gapRight : gapLeft;
                hostTrims.rightEdgeGaps.push({ start: gapStart, end: gapEnd });
              }
            }
          };

          // Create gaps in both walls - call twice with swapped roles
          createCrossGaps(wall1, geo1, geo2, trims[wall1.id]);
          createCrossGaps(wall2, geo2, geo1, trims[wall2.id]);
        }
      }
    }
  }

  // Sort gaps by actual geometric position along each edge
  // The drawing goes from startLeft->endLeft and startRight->endRight
  // So we need to sort by distance from the start point
  Object.entries(trims).forEach(([wallId, trim]) => {
    const geo = trim.geo;
    if (!geo) return;

    // Sort left edge gaps by distance from startLeft
    trim.leftEdgeGaps.sort((a, b) => {
      const distA = Math.sqrt((a.start.x - geo.startLeft.x)**2 + (a.start.y - geo.startLeft.y)**2);
      const distB = Math.sqrt((b.start.x - geo.startLeft.x)**2 + (b.start.y - geo.startLeft.y)**2);
      return distA - distB;
    });

    // Sort right edge gaps by distance from startRight
    trim.rightEdgeGaps.sort((a, b) => {
      const distA = Math.sqrt((a.start.x - geo.startRight.x)**2 + (a.start.y - geo.startRight.y)**2);
      const distB = Math.sqrt((b.start.x - geo.startRight.x)**2 + (b.start.y - geo.startRight.y)**2);
      return distA - distB;
    });

    if (DEBUG && (trim.leftEdgeGaps.length > 0 || trim.rightEdgeGaps.length > 0)) {
      trim.leftEdgeGaps.forEach((g, i) => {
        const dist = Math.sqrt((g.start.x - geo.startLeft.x)**2 + (g.start.y - geo.startLeft.y)**2);
      });
      trim.rightEdgeGaps.forEach((g, i) => {
        const dist = Math.sqrt((g.start.x - geo.startRight.x)**2 + (g.start.y - geo.startRight.y)**2);
      });
    }

    // Sort finish gaps similarly
    if (geo.startLeftFinish) {
      trim.leftFinishGaps.sort((a, b) => {
        const distA = Math.sqrt((a.start.x - geo.startLeftFinish.x)**2 + (a.start.y - geo.startLeftFinish.y)**2);
        const distB = Math.sqrt((b.start.x - geo.startLeftFinish.x)**2 + (b.start.y - geo.startLeftFinish.y)**2);
        return distA - distB;
      });
    }
    if (geo.startRightFinish) {
      trim.rightFinishGaps.sort((a, b) => {
        const distA = Math.sqrt((a.start.x - geo.startRightFinish.x)**2 + (a.start.y - geo.startRightFinish.y)**2);
        const distB = Math.sqrt((b.start.x - geo.startRightFinish.x)**2 + (b.start.y - geo.startRightFinish.y)**2);
        return distA - distB;
      });
    }

    // Merge overlapping gaps to prevent stray lines when two collinear walls
    // independently create T-junctions at the same point on a continuous wall
    const mergeGaps = (gaps, refPt, dirX, dirY) => {
      if (gaps.length < 2) return gaps;
      const merged = [gaps[0]];
      for (let k = 1; k < gaps.length; k++) {
        const prev = merged[merged.length - 1];
        const curr = gaps[k];
        // Project gap endpoints onto the wall direction to check overlap
        const prevEndT = (prev.end.x - refPt.x) * dirX + (prev.end.y - refPt.y) * dirY;
        const currStartT = (curr.start.x - refPt.x) * dirX + (curr.start.y - refPt.y) * dirY;
        if (currStartT <= prevEndT + 1) {
          // Overlapping or adjacent - merge by extending prev to the further end
          const currEndT = (curr.end.x - refPt.x) * dirX + (curr.end.y - refPt.y) * dirY;
          if (currEndT > prevEndT) {
            prev.end = curr.end;
          }
        } else {
          merged.push(curr);
        }
      }
      return merged;
    };
    trim.leftEdgeGaps = mergeGaps(trim.leftEdgeGaps, geo.startLeft, geo.dirX, geo.dirY);
    trim.rightEdgeGaps = mergeGaps(trim.rightEdgeGaps, geo.startRight, geo.dirX, geo.dirY);
    if (geo.startLeftFinish) {
      trim.leftFinishGaps = mergeGaps(trim.leftFinishGaps, geo.startLeftFinish, geo.dirX, geo.dirY);
    }
    if (geo.startRightFinish) {
      trim.rightFinishGaps = mergeGaps(trim.rightFinishGaps, geo.startRightFinish, geo.dirX, geo.dirY);
    }

    if (DEBUG && (trim.leftFinishGaps.length > 0 || trim.rightFinishGaps.length > 0)) {
      trim.leftFinishGaps.forEach((g, i) => {
      });
      trim.rightFinishGaps.forEach((g, i) => {
      });
    }
  });

  // Debug: Summary of walls with missing finish trims (will show lime/cyan dots)
  if (DEBUG) {
    walls.forEach(wall => {
      const trim = trims[wall.id];
      const geo = geos[wall.id];
      if (!geo) return;

      const issues = [];
      if (geo.startLeftFinish && !trim.startLeftFinishTrim && !trim.startHasT && !trim.startHasCollinear) {
        issues.push('startLeft');
      }
      if (geo.startRightFinish && !trim.startRightFinishTrim && !trim.startHasT && !trim.startHasCollinear) {
        issues.push('startRight');
      }
      if (geo.endLeftFinish && !trim.endLeftFinishTrim && !trim.endHasT && !trim.endHasCollinear) {
        issues.push('endLeft');
      }
      if (geo.endRightFinish && !trim.endRightFinishTrim && !trim.endHasT && !trim.endHasCollinear) {
        issues.push('endRight');
      }

      if (issues.length > 0) {
      }
    });
  }

  return trims;
}

/**
 * Draw architectural walls with pre-computed trim points
 * Uses two-pass rendering: fills first, then edges, to prevent overlap issues
 */
export function drawArchitecturalWalls(ctx, walls, trims, selectedItems = [], thinLines = false, layerColor = null) {
  if (!walls || walls.length === 0) return;

  // Line width multiplier for thin lines mode (like Revit's Thin Lines toggle)
  const lineScale = thinLines ? 0.5 : 1;

  // Pre-compute wall data for both passes
  const wallData = walls.map(wall => {
    const geo = trims[wall.id]?.geo || getWallGeometry(wall);
    if (!geo) return null;

    const isSelected = selectedItems.some(s => s.type === 'wall' && s.item?.id === wall.id);
    const trim = trims[wall.id] || { leftEdgeGaps: [], rightEdgeGaps: [] };

    // Get trimmed edge endpoints
    const startLeft = trim.startLeftTrim || geo.startLeft;
    const startRight = trim.startRightTrim || geo.startRight;
    const endLeft = trim.endLeftTrim || geo.endLeft;
    const endRight = trim.endRightTrim || geo.endRight;

    if (DEBUG && trim.endLeftFinishTrim) {
    }

    return { wall, geo, isSelected, trim, startLeft, startRight, endLeft, endRight };
  }).filter(Boolean);

  // PASS 1: Draw all wall fills first
  wallData.forEach(({ wall, startLeft, startRight, endLeft, endRight, trim }) => {
    if (DEBUG && (trim.startLeftTrim || trim.startRightTrim || trim.endLeftTrim || trim.endRightTrim)) {
    }

    // Fill the wall polygon - use layer color if provided, otherwise white
    ctx.fillStyle = layerColor || '#ffffff';
    ctx.beginPath();
    ctx.moveTo(startLeft.x, startLeft.y);
    ctx.lineTo(endLeft.x, endLeft.y);
    ctx.lineTo(endRight.x, endRight.y);
    ctx.lineTo(startRight.x, startRight.y);
    ctx.closePath();
    ctx.fill();
  });

  // PASS 2: Draw all wall edges (on top of all fills)
  // Calculate outline color based on fill color brightness
  const getOutlineColor = (fillColor) => {
    if (!fillColor || fillColor === '#ffffff') return '#000000';
    // For colored walls, use a darker version of the same color for outline
    const hex = fillColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Darken by 40%
    const darken = (c) => Math.max(0, Math.floor(c * 0.6));
    return `rgb(${darken(r)}, ${darken(g)}, ${darken(b)})`;
  };
  const outlineColor = getOutlineColor(layerColor);

  wallData.forEach(({ wall, geo, isSelected, trim, startLeft, startRight, endLeft, endRight }) => {
    ctx.save();

    // Draw the edge lines
    ctx.strokeStyle = isSelected ? '#00ffaa' : outlineColor;
    ctx.lineWidth = (isSelected ? 2 : 1) * lineScale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (DEBUG && (trim.startLeftTrim || trim.endLeftTrim || trim.startRightTrim || trim.endRightTrim || trim.startHasT || trim.endHasT)) {
    }

    // Draw left edge with gaps for T-intersections
    if (trim.leftEdgeGaps && trim.leftEdgeGaps.length > 0) {
      // Draw segments between gaps
      let currentStart = startLeft;
      for (const gap of trim.leftEdgeGaps) {
        // Draw from current start to gap start
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(gap.start.x, gap.start.y);
        ctx.stroke();
        // Move current start to gap end
        currentStart = gap.end;
      }
      // Draw from last gap end to end of edge
      ctx.beginPath();
      ctx.moveTo(currentStart.x, currentStart.y);
      ctx.lineTo(endLeft.x, endLeft.y);
      ctx.stroke();
    } else {
      // No gaps - draw full edge
      ctx.beginPath();
      ctx.moveTo(startLeft.x, startLeft.y);
      ctx.lineTo(endLeft.x, endLeft.y);
      ctx.stroke();
    }

    // Draw right edge with gaps for T-intersections
    if (trim.rightEdgeGaps && trim.rightEdgeGaps.length > 0) {
      let currentStart = startRight;
      for (const gap of trim.rightEdgeGaps) {
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(gap.start.x, gap.start.y);
        ctx.stroke();
        currentStart = gap.end;
      }
      ctx.beginPath();
      ctx.moveTo(currentStart.x, currentStart.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(startRight.x, startRight.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    }

    // End caps only if not trimmed, not T-connected, and not collinear junction
    const drawStartCap = !trim.startLeftTrim && !trim.startRightTrim && !trim.startHasT && !trim.startHasCollinear;
    const drawEndCap = !trim.endLeftTrim && !trim.endRightTrim && !trim.endHasT && !trim.endHasCollinear;

    if (DEBUG && (trim.startHasT || trim.endHasT)) {
    }

    if (drawStartCap) {
      ctx.beginPath();
      ctx.moveTo(startLeft.x, startLeft.y);
      ctx.lineTo(startRight.x, startRight.y);
      ctx.stroke();
    }

    if (drawEndCap) {
      ctx.beginPath();
      ctx.moveTo(endLeft.x, endLeft.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    }

    // DEBUG: Draw red circles at trim points to visualize where walls are being trimmed
    const DEBUG_TRIMS = false;
    if (DEBUG_TRIMS && (trim.endLeftTrim || trim.endRightTrim || trim.startLeftTrim || trim.startRightTrim)) {
      ctx.fillStyle = 'red';
      if (trim.endLeftTrim) {
        ctx.beginPath();
        ctx.arc(trim.endLeftTrim.x, trim.endLeftTrim.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      if (trim.endRightTrim) {
        ctx.beginPath();
        ctx.arc(trim.endRightTrim.x, trim.endRightTrim.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = 'blue';
      if (trim.startLeftTrim) {
        ctx.beginPath();
        ctx.arc(trim.startLeftTrim.x, trim.startLeftTrim.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      if (trim.startRightTrim) {
        ctx.beginPath();
        ctx.arc(trim.startRightTrim.x, trim.startRightTrim.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw finish lines (drywall/siding) if present
    // Use thinner lines for finish details
    ctx.lineWidth = (isSelected ? 1.5 : 0.75) * lineScale;

    // Debug: draw small circles at finish trim points
    // Red = left finish trim set, Blue = right finish trim set
    // Green = NO trim set (will draw bad end cap)
    const debugFinish = false;
    if (debugFinish) {
      // Show where trims ARE set (good)
      ctx.fillStyle = 'red';
      if (trim.startLeftFinishTrim) {
        ctx.beginPath();
        ctx.arc(trim.startLeftFinishTrim.x, trim.startLeftFinishTrim.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      if (trim.endLeftFinishTrim) {
        ctx.beginPath();
        ctx.arc(trim.endLeftFinishTrim.x, trim.endLeftFinishTrim.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = 'blue';
      if (trim.startRightFinishTrim) {
        ctx.beginPath();
        ctx.arc(trim.startRightFinishTrim.x, trim.startRightFinishTrim.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      if (trim.endRightFinishTrim) {
        ctx.beginPath();
        ctx.arc(trim.endRightFinishTrim.x, trim.endRightFinishTrim.y, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      // Show where trims are MISSING at corners (bad - will draw end cap)
      ctx.fillStyle = 'lime';
      if (!trim.startLeftFinishTrim && !trim.startHasT && !trim.startHasCollinear && geo.startLeftFinish) {
        ctx.beginPath();
        ctx.arc(geo.startLeftFinish.x, geo.startLeftFinish.y, 6, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!trim.endLeftFinishTrim && !trim.endHasT && !trim.endHasCollinear && geo.endLeftFinish) {
        ctx.beginPath();
        ctx.arc(geo.endLeftFinish.x, geo.endLeftFinish.y, 6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = 'cyan';
      if (!trim.startRightFinishTrim && !trim.startHasT && !trim.startHasCollinear && geo.startRightFinish) {
        ctx.beginPath();
        ctx.arc(geo.startRightFinish.x, geo.startRightFinish.y, 6, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!trim.endRightFinishTrim && !trim.endHasT && !trim.endHasCollinear && geo.endRightFinish) {
        ctx.beginPath();
        ctx.arc(geo.endRightFinish.x, geo.endRightFinish.y, 6, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Left finish line (interior drywall or exterior siding)
    if (geo.startLeftFinish && geo.endLeftFinish) {
      const startLeftFinish = trim.startLeftFinishTrim || geo.startLeftFinish;
      const endLeftFinish = trim.endLeftFinishTrim || geo.endLeftFinish;

      // Debug: show finish trim info for walls with T-junctions
      if (trim.startHasT || trim.endHasT) {
      }

      if (trim.startHasT || trim.endHasT) {
      }
      if (trim.leftFinishGaps && trim.leftFinishGaps.length > 0) {
        let currentStart = startLeftFinish;
        for (const gap of trim.leftFinishGaps) {
          ctx.beginPath();
          ctx.moveTo(currentStart.x, currentStart.y);
          ctx.lineTo(gap.start.x, gap.start.y);
          ctx.stroke();
          currentStart = gap.end;
        }
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(endLeftFinish.x, endLeftFinish.y);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(startLeftFinish.x, startLeftFinish.y);
        ctx.lineTo(endLeftFinish.x, endLeftFinish.y);
        ctx.stroke();
      }

      // Draw finish end caps at untrimmed ends (connect finish to outer edge)
      if (!trim.startLeftFinishTrim && !trim.startHasT && !trim.startHasCollinear) {
        ctx.beginPath();
        ctx.moveTo(geo.startLeft.x, geo.startLeft.y);
        ctx.lineTo(geo.startLeftFinish.x, geo.startLeftFinish.y);
        ctx.stroke();
      }
      if (!trim.endLeftFinishTrim && !trim.endHasT && !trim.endHasCollinear) {
        ctx.beginPath();
        ctx.moveTo(geo.endLeft.x, geo.endLeft.y);
        ctx.lineTo(geo.endLeftFinish.x, geo.endLeftFinish.y);
        ctx.stroke();
      }

      // T-junction connectors removed - finish lines now intersect directly with host finish lines
    }

    // Right finish line (interior drywall)
    if (geo.startRightFinish && geo.endRightFinish) {
      const startRightFinish = trim.startRightFinishTrim || geo.startRightFinish;
      const endRightFinish = trim.endRightFinishTrim || geo.endRightFinish;

      // Debug: show finish trim info for walls with T-junctions
      if (trim.startHasT || trim.endHasT) {
      }

      if (trim.rightFinishGaps && trim.rightFinishGaps.length > 0) {
        let currentStart = startRightFinish;
        for (const gap of trim.rightFinishGaps) {
          ctx.beginPath();
          ctx.moveTo(currentStart.x, currentStart.y);
          ctx.lineTo(gap.start.x, gap.start.y);
          ctx.stroke();
          currentStart = gap.end;
        }
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(endRightFinish.x, endRightFinish.y);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(startRightFinish.x, startRightFinish.y);
        ctx.lineTo(endRightFinish.x, endRightFinish.y);
        ctx.stroke();
      }

      // Draw finish end caps at untrimmed ends (connect finish to outer edge)
      if (!trim.startRightFinishTrim && !trim.startHasT && !trim.startHasCollinear) {
        ctx.beginPath();
        ctx.moveTo(geo.startRight.x, geo.startRight.y);
        ctx.lineTo(geo.startRightFinish.x, geo.startRightFinish.y);
        ctx.stroke();
      }
      if (!trim.endRightFinishTrim && !trim.endHasT && !trim.endHasCollinear) {
        ctx.beginPath();
        ctx.moveTo(geo.endRight.x, geo.endRight.y);
        ctx.lineTo(geo.endRightFinish.x, geo.endRightFinish.y);
        ctx.stroke();
      }

      // Draw interior T-junction connectors (short lines closing off the gap)
      if (trim.interiorTConnectors && trim.interiorTConnectors.length > 0) {
        for (const connector of trim.interiorTConnectors) {
          ctx.beginPath();
          ctx.moveTo(connector.from.x, connector.from.y);
          ctx.lineTo(connector.to.x, connector.to.y);
          ctx.stroke();
        }
      }
    }

    ctx.restore();
  });
}

// Keep the old function for backward compatibility
export function drawWallCorners(ctx, walls, selectedItems = []) {
  // No longer needed
}

/**
 * Draw standard walls with pre-computed trim points and colored layers
 */
export function drawStandardWalls(ctx, walls, trims, selectedItems = [], thinLines = false, layerColor = null) {
  if (!walls || walls.length === 0) return;

  const lineScale = thinLines ? 0.5 : 1;

  walls.forEach(wall => {
    const geo = trims[wall.id]?.geo || getWallGeometry(wall);
    if (!geo) return;

    const isSelected = selectedItems.some(s => s.type === 'wall' && s.item?.id === wall.id);
    const trim = trims[wall.id] || { leftEdgeGaps: [], rightEdgeGaps: [] };

    // Get wall config for layers
    const wallConfig = WALL_LAYERS[wall.type] || WALL_LAYERS['interior'];
    const thicknessInches = WALL_THICKNESS_OPTIONS[wall.type]?.thickness || 8;
    const thickness = inchesToPixels(thicknessInches);
    const totalLayerThickness = wallConfig.layers.reduce((sum, l) => sum + l.thickness, 0);
    const scaleFactor = thickness / totalLayerThickness;

    // Get trimmed edge endpoints for L-corner mitering
    const startLeft = trim.startLeftTrim || geo.startLeft;
    const startRight = trim.startRightTrim || geo.startRight;
    const endLeft = trim.endLeftTrim || geo.endLeft;
    const endRight = trim.endRightTrim || geo.endRight;

    ctx.save();

    // Build the wall polygon path for clipping using trimmed corners
    ctx.beginPath();
    ctx.moveTo(startLeft.x, startLeft.y);
    ctx.lineTo(endLeft.x, endLeft.y);
    ctx.lineTo(endRight.x, endRight.y);
    ctx.lineTo(startRight.x, startRight.y);
    ctx.closePath();
    ctx.clip();

    // Calculate wall direction and perpendicular
    const dx = wall.end.x - wall.start.x;
    const dy = wall.end.y - wall.start.y;
    const wallLength = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Draw in wall-local coordinates
    ctx.translate(wall.start.x, wall.start.y);
    ctx.rotate(angle);

    // If wall is flipped, reverse the layer order
    const layersToRender = wall.flipped ? [...wallConfig.layers].reverse() : wallConfig.layers;

    // Draw each layer as a colored band
    let yOffset = -thickness / 2;
    layersToRender.forEach(layer => {
      const layerThickness = layer.thickness * scaleFactor;
      ctx.fillStyle = isSelected ? adjustColor(layer.color, 30) : layer.color;
      ctx.fillRect(-50, yOffset, wallLength + 100, layerThickness); // Extend past ends (clipping will trim)
      yOffset += layerThickness;
    });

    // Draw layer separators
    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 0.5 * lineScale;
    yOffset = -thickness / 2;
    layersToRender.forEach((layer, i) => {
      if (i > 0) {
        ctx.beginPath();
        ctx.moveTo(-50, yOffset);
        ctx.lineTo(wallLength + 100, yOffset);
        ctx.stroke();
      }
      yOffset += layer.thickness * scaleFactor;
    });

    ctx.restore();

    // Now draw the outline edges (outside of clip)
    ctx.save();
    ctx.strokeStyle = isSelected ? '#00ffaa' : '#555555';
    ctx.lineWidth = (isSelected ? 2 : 1) * lineScale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Draw left edge with gaps for T-intersections
    if (trim.leftEdgeGaps && trim.leftEdgeGaps.length > 0) {
      let currentStart = startLeft;
      for (const gap of trim.leftEdgeGaps) {
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(gap.start.x, gap.start.y);
        ctx.stroke();
        currentStart = gap.end;
      }
      ctx.beginPath();
      ctx.moveTo(currentStart.x, currentStart.y);
      ctx.lineTo(endLeft.x, endLeft.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(startLeft.x, startLeft.y);
      ctx.lineTo(endLeft.x, endLeft.y);
      ctx.stroke();
    }

    // Draw right edge with gaps for T-intersections
    if (trim.rightEdgeGaps && trim.rightEdgeGaps.length > 0) {
      let currentStart = startRight;
      for (const gap of trim.rightEdgeGaps) {
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(gap.start.x, gap.start.y);
        ctx.stroke();
        currentStart = gap.end;
      }
      ctx.beginPath();
      ctx.moveTo(currentStart.x, currentStart.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(startRight.x, startRight.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    }

    // End caps only if not trimmed (L-corner), not T-connected, and not collinear junction
    const drawStartCap = !trim.startLeftTrim && !trim.startRightTrim && !trim.startHasT && !trim.startHasCollinear;
    const drawEndCap = !trim.endLeftTrim && !trim.endRightTrim && !trim.endHasT && !trim.endHasCollinear;

    if (drawStartCap) {
      ctx.beginPath();
      ctx.moveTo(startLeft.x, startLeft.y);
      ctx.lineTo(startRight.x, startRight.y);
      ctx.stroke();
    }

    if (drawEndCap) {
      ctx.beginPath();
      ctx.moveTo(endLeft.x, endLeft.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    }

    ctx.restore();
  });
}

/**
 * Draw detailed walls with pre-computed trim points, colored layers, patterns, and studs
 */
export function drawDetailedWalls(ctx, walls, trims, selectedItems = [], thinLines = false, layerColor = null) {
  if (!walls || walls.length === 0) return;

  const lineScale = thinLines ? 0.5 : 1;

  walls.forEach(wall => {
    const geo = trims[wall.id]?.geo || getWallGeometry(wall);
    if (!geo) return;

    const isSelected = selectedItems.some(s => s.type === 'wall' && s.item?.id === wall.id);
    const trim = trims[wall.id] || { leftEdgeGaps: [], rightEdgeGaps: [] };

    // Get wall config for layers
    const wallConfig = WALL_LAYERS[wall.type] || WALL_LAYERS['interior'];
    const thicknessInches = WALL_THICKNESS_OPTIONS[wall.type]?.thickness || 8;
    const thickness = inchesToPixels(thicknessInches);
    const totalLayerThickness = wallConfig.layers.reduce((sum, l) => sum + l.thickness, 0);
    const scaleFactor = thickness / totalLayerThickness;

    // Get trimmed edge endpoints for L-corner mitering
    const startLeft = trim.startLeftTrim || geo.startLeft;
    const startRight = trim.startRightTrim || geo.startRight;
    const endLeft = trim.endLeftTrim || geo.endLeft;
    const endRight = trim.endRightTrim || geo.endRight;

    ctx.save();

    // Build the wall polygon path for clipping using trimmed corners
    ctx.beginPath();
    ctx.moveTo(startLeft.x, startLeft.y);
    ctx.lineTo(endLeft.x, endLeft.y);
    ctx.lineTo(endRight.x, endRight.y);
    ctx.lineTo(startRight.x, startRight.y);
    ctx.closePath();
    ctx.clip();

    // Calculate wall direction and perpendicular
    const dx = wall.end.x - wall.start.x;
    const dy = wall.end.y - wall.start.y;
    const wallLength = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Calculate pattern offset based on wall's global position
    const patternOffset = (wall.start.x + wall.start.y) % 48;

    // Draw in wall-local coordinates
    ctx.translate(wall.start.x, wall.start.y);
    ctx.rotate(angle);

    // If wall is flipped, reverse the layer order
    const layersToRender = wall.flipped ? [...wallConfig.layers].reverse() : wallConfig.layers;

    // Draw each layer with patterns
    let yOffset = -thickness / 2;
    layersToRender.forEach(layer => {
      const layerThickness = layer.thickness * scaleFactor;

      ctx.save();
      ctx.translate(0, yOffset);

      // Base fill
      ctx.fillStyle = isSelected ? adjustColor(layer.color, 20) : layer.color;
      ctx.fillRect(-50, 0, wallLength + 100, layerThickness); // Extend past ends (clipping will trim)

      // Draw pattern based on layer type
      if (layer.pattern === 'siding') {
        ctx.strokeStyle = 'rgba(0,0,0,0.2)';
        ctx.lineWidth = 0.5;
        const lineSpacing = Math.max(1.5, layerThickness / 4);
        for (let y = lineSpacing; y < layerThickness; y += lineSpacing) {
          ctx.beginPath();
          ctx.moveTo(-50, y);
          ctx.lineTo(wallLength + 100, y);
          ctx.stroke();
        }
      } else if (layer.pattern === 'plywood') {
        ctx.strokeStyle = 'rgba(139,90,43,0.3)';
        ctx.lineWidth = 0.5;
        const plywoodSpacing = 24;
        const startX = -((patternOffset) % plywoodSpacing);
        for (let x = startX; x < wallLength + 100; x += plywoodSpacing) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, layerThickness);
          ctx.stroke();
        }
      } else if (layer.pattern === 'insulation') {
        ctx.strokeStyle = 'rgba(255,150,180,0.4)';
        ctx.lineWidth = 1;
        const waveHeight = layerThickness * 0.3;
        const waveSpacing = 8;
        const startX = -((patternOffset) % waveSpacing);
        for (let x = startX; x < wallLength + 100; x += waveSpacing) {
          ctx.beginPath();
          ctx.moveTo(x, layerThickness / 2 - waveHeight);
          ctx.quadraticCurveTo(x + 4, layerThickness / 2 + waveHeight, x + waveSpacing, layerThickness / 2 - waveHeight);
          ctx.stroke();
        }
      } else if (layer.pattern === 'cavity') {
        ctx.fillStyle = isSelected ? '#3a3a3a' : '#1a1a1a';
        ctx.fillRect(-50, 0, wallLength + 100, layerThickness);
      }

      ctx.restore();
      yOffset += layerThickness;
    });

    // Draw studs in detailed mode
    if (wallConfig.studs) {
      // Stud spacing is in inches, convert to pixels (GRID_SIZE pixels = 6 inches)
      const studSpacing = wallConfig.studs.spacing * (GRID_SIZE / 6);
      // Stud width (1.5" for 2x4/2x6) converted to pixels
      const studWidth = wallConfig.studs.width * (GRID_SIZE / 6);
      const studDepth = wallConfig.studs.depth * scaleFactor;

      // Find the cavity layer position
      let cavityStart = -thickness / 2;
      let cavityThickness = 0;
      wallConfig.layers.forEach(layer => {
        const layerThick = layer.thickness * scaleFactor;
        if (layer.pattern === 'cavity' || layer.pattern === 'insulation') {
          cavityThickness = layerThick;
        } else if (cavityThickness === 0) {
          cavityStart += layerThick;
        }
      });

      // Draw studs
      ctx.fillStyle = isSelected ? '#E8C888' : wallConfig.studs.color;
      const studStartOffset = (patternOffset % studSpacing);

      // End stud at start
      ctx.fillRect(-50, cavityStart, studWidth + 50, cavityThickness);

      // Interior studs
      const firstStudX = studSpacing - studStartOffset;
      for (let x = firstStudX; x < wallLength + 50; x += studSpacing) {
        ctx.fillRect(x - studWidth / 2, cavityStart, studWidth, cavityThickness);
      }

      // End stud at end
      ctx.fillRect(wallLength - studWidth, cavityStart, studWidth + 50, cavityThickness);
    }

    // Draw layer separators
    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 0.5 * lineScale;
    yOffset = -thickness / 2;
    layersToRender.forEach((layer, i) => {
      if (i > 0) {
        ctx.beginPath();
        ctx.moveTo(-50, yOffset);
        ctx.lineTo(wallLength + 100, yOffset);
        ctx.stroke();
      }
      yOffset += layer.thickness * scaleFactor;
    });

    ctx.restore();

    // Now draw the outline edges (outside of clip)
    ctx.save();
    ctx.strokeStyle = isSelected ? '#00ffaa' : '#555555';
    ctx.lineWidth = (isSelected ? 2 : 1.5) * lineScale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Draw left edge with gaps for T-intersections
    if (trim.leftEdgeGaps && trim.leftEdgeGaps.length > 0) {
      let currentStart = startLeft;
      for (const gap of trim.leftEdgeGaps) {
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(gap.start.x, gap.start.y);
        ctx.stroke();
        currentStart = gap.end;
      }
      ctx.beginPath();
      ctx.moveTo(currentStart.x, currentStart.y);
      ctx.lineTo(endLeft.x, endLeft.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(startLeft.x, startLeft.y);
      ctx.lineTo(endLeft.x, endLeft.y);
      ctx.stroke();
    }

    // Draw right edge with gaps for T-intersections
    if (trim.rightEdgeGaps && trim.rightEdgeGaps.length > 0) {
      let currentStart = startRight;
      for (const gap of trim.rightEdgeGaps) {
        ctx.beginPath();
        ctx.moveTo(currentStart.x, currentStart.y);
        ctx.lineTo(gap.start.x, gap.start.y);
        ctx.stroke();
        currentStart = gap.end;
      }
      ctx.beginPath();
      ctx.moveTo(currentStart.x, currentStart.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(startRight.x, startRight.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    }

    // End caps only if not trimmed (L-corner), not T-connected, and not collinear junction
    const drawStartCap = !trim.startLeftTrim && !trim.startRightTrim && !trim.startHasT && !trim.startHasCollinear;
    const drawEndCap = !trim.endLeftTrim && !trim.endRightTrim && !trim.endHasT && !trim.endHasCollinear;

    if (drawStartCap) {
      ctx.beginPath();
      ctx.moveTo(startLeft.x, startLeft.y);
      ctx.lineTo(startRight.x, startRight.y);
      ctx.stroke();
    }

    if (drawEndCap) {
      ctx.beginPath();
      ctx.moveTo(endLeft.x, endLeft.y);
      ctx.lineTo(endRight.x, endRight.y);
      ctx.stroke();
    }

    ctx.restore();
  });
}
